/**
 * Session 30 — Retail POS Dashboard.
 *
 * A fast, counter-first point-of-sale screen that reuses the existing
 * order pipeline: every sale opens a hidden retail table-session +
 * order, so Kitchen / Bar / Runner dashboards and all realtime sync
 * work unchanged. Kitchen/bar items show there as "Retail #N".
 *
 * Self-contained on purpose — the App.js monolith only mounts this via
 * a thin wrapper (session boot + logout). No imports FROM App.js to
 * avoid circular dependencies.
 */
import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import {
  Banknote,
  Check,
  ChefHat,
  ClipboardList,
  CreditCard,
  History,
  Landmark,
  Loader2,
  LogOut,
  Minus,
  Moon,
  Plus,
  Phone,
  Printer,
  QrCode,
  Search,
  ShieldAlert,
  ShoppingBag,
  SlidersHorizontal,
  Split,
  Sun,
  Trash2,
  UtensilsCrossed,
  Wifi,
  WifiOff,
  X,
  Zap,
} from "lucide-react";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Skeleton } from "@/components/ui/skeleton";
import { ScrollArea } from "@/components/ui/scroll-area";
import { Switch } from "@/components/ui/switch";
import { realtime, useRealtimeConnection, useRealtimeEvent } from "@/lib/realtime";
import { MeshStatusPill } from "@/components/MeshStatusPill";
import { PrinterSettingsDialog } from "@/components/PrinterSettingsDialog";
import { FullscreenButton } from "@/components/FullscreenButton";
import { PendingApprovalsCard } from "@/components/PendingApprovalsCard";
import { printSlip } from "@/lib/printer";
import { playOrderReadyChime } from "@/lib/notify";
import { localWrite, LOCAL_EVENT_APPLIED } from "@/lib/eventEngine";
import { EventType } from "@/lib/events";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const formatPrice = (value) => {
  const numeric = Number(value || 0);
  const [whole, decimal] = numeric.toFixed(2).split(".");
  return `R ${Number(whole).toLocaleString("en-US")}.${decimal}`;
};

/** Session 30.4 — Items with configurable sides / prep questions / notes
 *  open a small options sheet instead of being blocked. Returns true when
 *  the item has anything to ask. */
const hasSideChoices = (item) => {
  const sc = item?.sides_config || {};
  return sc.selection_mode && sc.selection_mode !== "none" && Array.isArray(sc.allowed_side_ids) && sc.allowed_side_ids.length > 0;
};
const prepPromptsOf = (item) => (Array.isArray(item?.prep_prompts) ? item.prep_prompts.filter((p) => p && p.label && Array.isArray(p.options) && p.options.length) : []);
const notesAllowed = (item) => ["staff", "all"].includes(String(item?.notes_mode || "off").toLowerCase());
const needsOptions = (item) => hasSideChoices(item) || prepPromptsOf(item).length > 0;

/** Deterministic cart line key so identical configurations merge. */
const buildLineKey = (itemId, sideIds = [], prep = [], note = "") => {
  const s = [...(sideIds || [])].map(String).sort().join(",");
  const p = [...(prep || [])].map((x) => `${x.label}=${x.choice}`).sort().join("|");
  const n = String(note || "").trim().toLowerCase();
  return [String(itemId), s && `s:${s}`, p && `p:${p}`, n && `n:${n}`].filter(Boolean).join("#");
};

/** First `free_count` picks are free; the rest are charged. Mirrors backend. */
const computeExtras = (item, sideIds, sidesLibrary) => {
  const cfg = item?.sides_config || {};
  const free = Math.max(0, Number(cfg.free_count) || 0);
  const lib = {};
  (sidesLibrary || []).forEach((s) => { if (s?.id) lib[String(s.id)] = s; });
  let perUnit = 0;
  const snapshots = (sideIds || []).map((id, idx) => {
    const side = lib[String(id)];
    if (!side) return null;
    const price = Number(side.price || 0);
    const charged = idx >= free;
    if (charged) perUnit += price;
    return { id: String(id), name: side.name, price, charged };
  }).filter(Boolean);
  return { perUnit: Math.round(perUnit * 100) / 100, snapshots };
};

/** Product image with graceful fallback (same look as the digital menu). */
const ProductImage = ({ src, alt, className = "" }) => {
  const [failed, setFailed] = useState(false);
  useEffect(() => { setFailed(false); }, [src]);
  if (!src || failed) {
    return (
      <div className={`flex items-center justify-center bg-secondary/70 text-muted-foreground/50 ${className}`} data-testid="retail-product-image-placeholder">
        <UtensilsCrossed className="h-6 w-6" />
      </div>
    );
  }
  return <img src={src} alt={alt || ""} loading="lazy" onError={() => setFailed(true)} className={`object-cover ${className}`} data-testid="retail-product-image" />;
};

/**
 * Session 30.16 — Strict payment capture shared by walk-in sales and tab
 * settlement. The operator MUST type the amount for every method:
 *   cash  → cash received ≥ total (change shown)
 *   card  → exact card amount = total
 *   split → cash + card typed, both > 0, summing to the total
 * Nothing can be completed until `evaluatePayment(...).valid` is true, and
 * the caller shows an "Are you sure?" confirmation before charging.
 */
// Session 31.26 — Multi-tender: "eft" (exact, like card) and "multi" (a
// small ledger: cash / several cards / EFT rows, each with its own tip,
// that must add up to the total) join cash / card / split.
const EMPTY_PAY = {
  method: "cash", cashStr: "", cardStr: "", eftStr: "", splitCashStr: "", splitCardStr: "",
  rows: [],                                    // multi: [{method, amount, tip}]
  draft: { method: "card", amountStr: "", tipStr: "" }, // multi: row being typed
};
const _n = (v) => { const x = Number(String(v ?? "").trim()); return Number.isFinite(x) ? x : NaN; };
const _r2 = (v) => Math.round(v * 100) / 100;
const TENDER_LABEL = { cash: "Cash", card: "Card", eft: "EFT" };
/** Human label for a recorded sale/tab payment method. */
export const payMethodLabel = (m) => (m === "split" ? "Multiple" : m === "multi" ? "Multiple" : m === "card" ? "Card" : m === "eft" ? "EFT" : m === "unpaid" ? "Unpaid" : "Cash");

function evaluatePayment(pay, total) {
  const t = _r2(Number(total) || 0);
  const out = { valid: false, cash: 0, card: 0, eft: 0, tip: 0, change: 0, reason: "", payments: [] };
  if (t <= 0) { out.valid = true; return out; }
  if (pay.method === "cash") {
    const cash = _n(pay.cashStr);
    if (pay.cashStr === "" || Number.isNaN(cash)) { out.reason = "Type the cash amount received."; return out; }
    if (cash + 0.009 < t) { out.reason = `Cash received is less than the total (${_r2(t - cash).toFixed(2)} short).`; return out; }
    return { ...out, valid: true, cash: t, change: _r2(cash - t), tendered: cash, payments: [{ method: "cash", amount: t, tip: 0 }] };
  }
  if (pay.method === "card") {
    const card = _n(pay.cardStr);
    if (pay.cardStr === "" || Number.isNaN(card)) { out.reason = "Type the card amount charged."; return out; }
    if (Math.abs(card - t) > 0.009) { out.reason = `Card amount must be exactly the total (${t.toFixed(2)}).`; return out; }
    return { ...out, valid: true, card: t, payments: [{ method: "card", amount: t, tip: 0 }] };
  }
  if (pay.method === "eft") {
    const eft = _n(pay.eftStr);
    if (pay.eftStr === "" || Number.isNaN(eft)) { out.reason = "Type the EFT amount received."; return out; }
    if (Math.abs(eft - t) > 0.009) { out.reason = `EFT amount must be exactly the total (${t.toFixed(2)}).`; return out; }
    return { ...out, valid: true, eft: t, payments: [{ method: "eft", amount: t, tip: 0 }] };
  }
  if (pay.method === "multi") {
    const rows = (pay.rows || []).filter((r) => r && (Number(r.amount) || 0) > 0);
    if (!rows.length) { out.reason = "Add each payment (cash, card, EFT) until they cover the total."; return out; }
    const sum = _r2(rows.reduce((a, r) => a + (Number(r.amount) || 0), 0));
    if (sum + 0.009 < t) { out.reason = `${_r2(t - sum).toFixed(2)} still to be covered.`; return out; }
    if (sum - 0.009 > t) { out.reason = `Payments add up to ${sum.toFixed(2)} — more than the total (${t.toFixed(2)}). Remove or reduce one.`; return out; }
    const cash = _r2(rows.filter((r) => r.method === "cash").reduce((a, r) => a + r.amount, 0));
    const card = _r2(rows.filter((r) => r.method === "card").reduce((a, r) => a + r.amount, 0));
    const eft = _r2(rows.filter((r) => r.method === "eft").reduce((a, r) => a + r.amount, 0));
    const tip = _r2(rows.reduce((a, r) => a + (Number(r.tip) || 0), 0));
    return { ...out, valid: true, cash, card, eft, tip, payments: rows.map((r) => ({ method: r.method, amount: _r2(r.amount), tip: _r2(Number(r.tip) || 0) })) };
  }
  // split (legacy cash + card)
  const cash = _n(pay.splitCashStr);
  const card = _n(pay.splitCardStr);
  if (pay.splitCashStr === "" || pay.splitCardStr === "" || Number.isNaN(cash) || Number.isNaN(card)) { out.reason = "Type both the cash and the card amounts."; return out; }
  if (cash <= 0 || card <= 0) { out.reason = "Both portions must be more than zero."; return out; }
  if (Math.abs(_r2(cash + card) - t) > 0.009) { out.reason = `Cash + card must equal the total (${t.toFixed(2)}); currently ${_r2(cash + card).toFixed(2)}.`; return out; }
  return { ...out, valid: true, cash: _r2(cash), card: _r2(card), payments: [{ method: "cash", amount: _r2(cash), tip: 0 }, { method: "card", amount: _r2(card), tip: 0 }] };
}

function PaymentFields({ total, pay, onChange, testPrefix = "retail" }) {
  const ev = evaluatePayment(pay, total);
  const set = (patch) => onChange({ ...pay, ...patch });
  const cashNum = _n(pay.cashStr);
  const rows = pay.rows || [];
  const draft = pay.draft || EMPTY_PAY.draft;
  const rowsSum = _r2(rows.reduce((a, r) => a + (Number(r.amount) || 0), 0));
  const remaining = Math.max(0, _r2((Number(total) || 0) - rowsSum));
  const draftAmt = _n(draft.amountStr);
  const draftTip = Math.max(0, _n(draft.tipStr) || 0);
  const canAddRow = !Number.isNaN(draftAmt) && draftAmt > 0 && remaining > 0;
  const addRow = () => {
    if (!canAddRow) return;
    const amount = Math.min(_r2(draftAmt), remaining);
    const nextRows = [...rows, { _k: `k${Date.now()}${Math.random().toString(36).slice(2, 7)}`, method: draft.method, amount, tip: _r2(draftTip) }];
    const nextRemaining = Math.max(0, _r2(remaining - amount));
    set({ rows: nextRows, draft: { method: draft.method === "cash" ? "card" : draft.method, amountStr: nextRemaining > 0 ? String(nextRemaining.toFixed(2)) : "", tipStr: "" } });
  };
  const cardIndex = (idx) => rows.slice(0, idx + 1).filter((r) => r.method === "card").length;
  return (
    <div className="space-y-3" data-testid={`${testPrefix}-payment-fields`}>
      <div className="grid grid-cols-4 gap-1.5">
        {[
          { key: "cash", label: "Cash", icon: Banknote },
          { key: "card", label: "Card", icon: CreditCard },
          { key: "eft", label: "EFT", icon: Landmark },
          { key: "multi", label: "Multiple", icon: Split },
        ].map(({ key, label, icon: Icon }) => (
          <button
            key={key}
            type="button"
            onClick={() => set({ method: key, ...(key === "multi" && !rows.length ? { draft: { method: "card", amountStr: String(_r2(total).toFixed(2)), tipStr: "" } } : {}) })}
            className={`flex h-11 items-center justify-center gap-1.5 rounded-xl border text-sm font-medium transition-colors ${pay.method === key ? "border-transparent bg-[hsl(var(--primary))] text-primary-foreground" : "border-border bg-card hover:bg-secondary"}`}
            data-testid={`${testPrefix}-${key}-tab`}
          >
            <Icon className="h-4 w-4" />
            {label}
          </button>
        ))}
      </div>

      {pay.method === "cash" ? (
        <div className="space-y-2">
          <div className="flex flex-wrap gap-1.5">
            <button type="button" className="rounded-lg border border-border bg-card px-3 py-1.5 text-xs font-medium hover:bg-secondary" onClick={() => set({ cashStr: String(_r2(total)) })} data-testid={`${testPrefix}-tender-exact`}>Exact</button>
            {/* Session 31.6 — quick-tender notes (R50/R100/R200/R500) removed on request; "Exact" only. */}
          </div>
          <Input
            type="number" inputMode="decimal" min="0" step="0.01"
            placeholder="Cash received — required"
            value={pay.cashStr}
            onChange={(e) => set({ cashStr: e.target.value })}
            className={`h-11 rounded-xl tabular-nums ${pay.cashStr !== "" && !ev.valid ? "border-destructive focus-visible:ring-destructive" : ""}`}
            data-testid={`${testPrefix}-tender-input`}
          />
          {ev.valid && ev.change > 0 ? (
            <p className="text-sm font-semibold text-emerald-700 dark:text-emerald-300" data-testid={`${testPrefix}-change-due`}>Change: <span className="tabular-nums">{formatPrice(ev.change)}</span></p>
          ) : ev.valid && !Number.isNaN(cashNum) ? (
            <p className="text-xs font-medium text-emerald-700 dark:text-emerald-300">Exact amount — no change.</p>
          ) : null}
        </div>
      ) : null}

      {pay.method === "card" ? (
        <div className="space-y-2">
          <div className="flex flex-wrap gap-1.5">
            <button type="button" className="rounded-lg border border-border bg-card px-3 py-1.5 text-xs font-medium hover:bg-secondary" onClick={() => set({ cardStr: String(_r2(total)) })} data-testid={`${testPrefix}-card-exact`}>Exact {formatPrice(total)}</button>
          </div>
          <Input
            type="number" inputMode="decimal" min="0" step="0.01"
            placeholder="Card amount charged — required"
            value={pay.cardStr}
            onChange={(e) => set({ cardStr: e.target.value })}
            className={`h-11 rounded-xl tabular-nums ${pay.cardStr !== "" && !ev.valid ? "border-destructive focus-visible:ring-destructive" : ""}`}
            data-testid={`${testPrefix}-card-input`}
          />
        </div>
      ) : null}

      {pay.method === "eft" ? (
        <div className="space-y-2">
          <div className="flex flex-wrap gap-1.5">
            <button type="button" className="rounded-lg border border-border bg-card px-3 py-1.5 text-xs font-medium hover:bg-secondary" onClick={() => set({ eftStr: String(_r2(total)) })} data-testid={`${testPrefix}-eft-exact`}>Exact {formatPrice(total)}</button>
          </div>
          <Input
            type="number" inputMode="decimal" min="0" step="0.01"
            placeholder="EFT amount received — required"
            value={pay.eftStr}
            onChange={(e) => set({ eftStr: e.target.value })}
            className={`h-11 rounded-xl tabular-nums ${pay.eftStr !== "" && !ev.valid ? "border-destructive focus-visible:ring-destructive" : ""}`}
            data-testid={`${testPrefix}-eft-input`}
          />
          <p className="text-xs text-muted-foreground">Confirm the transfer shows in the bank before completing.</p>
        </div>
      ) : null}

      {pay.method === "multi" ? (
        <div className="space-y-2" data-testid={`${testPrefix}-multi-fields`}>
          {rows.length ? (
            <ul className="divide-y divide-border/60 rounded-xl border border-border bg-secondary/30" data-testid={`${testPrefix}-multi-rows`}>
              {rows.map((r, idx) => {
                const Icon = r.method === "cash" ? Banknote : r.method === "eft" ? Landmark : CreditCard;
                return (
                  <li key={r._k || `${r.method}-${r.amount}-${idx}`} className="flex items-center gap-2 px-3 py-1.5 text-sm" data-testid={`${testPrefix}-multi-row-${idx}`}>
                    <Icon className="h-4 w-4 shrink-0 text-muted-foreground" />
                    <span className="min-w-0 flex-1 truncate font-medium">{r.method === "card" ? `Card ${cardIndex(idx)}` : TENDER_LABEL[r.method]}{Number(r.tip) > 0 ? <span className="ml-1 text-xs font-normal text-muted-foreground">tip {formatPrice(r.tip)}</span> : null}</span>
                    <span className="tabular-nums font-semibold">{formatPrice(r.amount)}</span>
                    <button type="button" onClick={() => set({ rows: rows.filter((_, k) => k !== idx), draft: { ...draft, amountStr: String(_r2(remaining + r.amount).toFixed(2)) } })} className="rounded-md p-1 text-muted-foreground hover:bg-destructive/10 hover:text-destructive" aria-label="Remove payment" data-testid={`${testPrefix}-multi-remove-${idx}`}>
                      <X className="h-3.5 w-3.5" />
                    </button>
                  </li>
                );
              })}
            </ul>
          ) : null}
          <div className={`flex items-baseline justify-between rounded-xl px-3 py-1.5 text-sm ${remaining <= 0.009 && rows.length ? "bg-emerald-500/10 text-emerald-800 dark:text-emerald-200" : "bg-amber-500/10 text-amber-900 dark:text-amber-200"}`}>
            <span className="font-medium">{remaining <= 0.009 && rows.length ? "Covered" : "Still to cover"}</span>
            <span className="tabular-nums font-semibold" data-testid={`${testPrefix}-multi-remaining`}>{formatPrice(remaining)}</span>
          </div>
          {remaining > 0.009 ? (
            <div className="space-y-2 rounded-xl border border-dashed border-border p-2.5">
              <div className="grid grid-cols-3 gap-1.5">
                {["cash", "card", "eft"].map((m) => (
                  <button key={m} type="button" onClick={() => set({ draft: { ...draft, method: m } })} className={`h-9 rounded-lg border text-xs font-semibold transition-colors ${draft.method === m ? "border-transparent bg-[hsl(var(--accent))] text-[hsl(var(--accent-foreground))]" : "border-border bg-card hover:bg-secondary"}`} data-testid={`${testPrefix}-multi-method-${m}`}>
                    {m === "card" ? `Card ${rows.filter((r) => r.method === "card").length + 1}` : TENDER_LABEL[m]}
                  </button>
                ))}
              </div>
              <div className="grid grid-cols-[1fr_auto_auto] items-center gap-1.5">
                <Input type="number" inputMode="decimal" min="0" step="0.01" placeholder={`Amount (max ${remaining.toFixed(2)})`} value={draft.amountStr} onChange={(e) => set({ draft: { ...draft, amountStr: e.target.value } })} onKeyDown={(e) => { if (e.key === "Enter") { e.preventDefault(); addRow(); } }} className="h-10 rounded-lg tabular-nums" data-testid={`${testPrefix}-multi-amount-input`} />
                <Input type="number" inputMode="decimal" min="0" step="0.01" placeholder="Tip" value={draft.tipStr} onChange={(e) => set({ draft: { ...draft, tipStr: e.target.value } })} className="h-10 w-20 rounded-lg tabular-nums" data-testid={`${testPrefix}-multi-tip-input`} />
                <Button type="button" size="sm" className="h-10 rounded-lg" disabled={!canAddRow} onClick={addRow} data-testid={`${testPrefix}-multi-add`}>Add</Button>
              </div>
            </div>
          ) : null}
        </div>
      ) : null}

      {pay.method === "split" ? (
        <div className="grid grid-cols-2 gap-2">
          <div>
            <label className="text-xs font-medium text-muted-foreground">Cash portion</label>
            <Input type="number" inputMode="decimal" min="0" step="0.01" placeholder="Required" value={pay.splitCashStr}
              onChange={(e) => {
                const v = e.target.value; const c = _n(v);
                set({ splitCashStr: v, splitCardStr: !Number.isNaN(c) && c > 0 && c < total ? String(_r2(total - c)) : pay.splitCardStr });
              }}
              className="mt-1 h-11 rounded-xl tabular-nums" data-testid={`${testPrefix}-split-cash-input`} />
          </div>
          <div>
            <label className="text-xs font-medium text-muted-foreground">Card portion</label>
            <Input type="number" inputMode="decimal" min="0" step="0.01" placeholder="Required" value={pay.splitCardStr}
              onChange={(e) => set({ splitCardStr: e.target.value })}
              className="mt-1 h-11 rounded-xl tabular-nums" data-testid={`${testPrefix}-split-card-input`} />
          </div>
        </div>
      ) : null}

      {!ev.valid && total > 0 ? (
        <p className="text-xs font-medium text-destructive" data-testid={`${testPrefix}-payment-hint`}>{ev.reason}</p>
      ) : null}
    </div>
  );
}

/** "Are you sure?" gate shown before any money is recorded. */
function ConfirmPaymentDialog({ state, onCancel, onConfirm, busy }) {
  const open = Boolean(state?.open);
  const ev = state?.ev || {};
  const method = state?.method || "cash";
  const total = Number(state?.total || 0);
  const methodLabel = method === "cash" ? "CASH" : method === "card" ? "CARD" : method === "eft" ? "EFT" : method === "multi" ? "MULTI-TENDER" : "SPLIT (cash + card)";
  const payments = Array.isArray(ev.payments) ? ev.payments : [];
  let cardN = 0;
  return (
    <Dialog open={open} onOpenChange={(v) => { if (!v && !busy) onCancel(); }}>
      <DialogContent className="max-w-sm rounded-2xl" data-testid="retail-confirm-payment-dialog">
        <DialogHeader>
          <DialogTitle>Are you sure?</DialogTitle>
          <DialogDescription>
            You are about to record a <span className="font-semibold text-foreground">{methodLabel}</span> payment of{" "}
            <span className="font-semibold text-foreground tabular-nums">{formatPrice(total)}</span>
            {state?.label ? <> for <span className="font-semibold text-foreground">{state.label}</span></> : null}.
          </DialogDescription>
        </DialogHeader>
        <div className="space-y-1 rounded-xl bg-secondary/60 px-3 py-2 text-sm" data-testid="retail-confirm-payment-breakdown">
          {payments.length > 1 || method === "multi" ? payments.map((p, idx) => {
            if (p.method === "card") cardN += 1;
            const lbl = p.method === "card" ? `Card ${cardN}` : TENDER_LABEL[p.method] || p.method;
            return <div key={idx} className="flex justify-between"><span>{lbl}{Number(p.tip) > 0 ? <span className="text-muted-foreground"> (tip {formatPrice(p.tip)})</span> : null}</span><span className="tabular-nums font-semibold">{formatPrice(p.amount)}</span></div>;
          }) : (
            <>
              {ev.cash > 0 ? <div className="flex justify-between"><span>Cash</span><span className="tabular-nums font-semibold">{formatPrice(ev.cash)}</span></div> : null}
              {ev.card > 0 ? <div className="flex justify-between"><span>Card</span><span className="tabular-nums font-semibold">{formatPrice(ev.card)}</span></div> : null}
              {ev.eft > 0 ? <div className="flex justify-between"><span>EFT</span><span className="tabular-nums font-semibold">{formatPrice(ev.eft)}</span></div> : null}
            </>
          )}
          {ev.tendered != null && ev.tendered !== ev.cash ? <div className="flex justify-between"><span>Cash received</span><span className="tabular-nums">{formatPrice(ev.tendered)}</span></div> : null}
          {ev.tip > 0 ? <div className="flex justify-between"><span>Tips</span><span className="tabular-nums font-semibold">{formatPrice(ev.tip)}</span></div> : null}
          {ev.change > 0 ? <div className="flex justify-between text-emerald-700 dark:text-emerald-300"><span>Change to give</span><span className="tabular-nums font-semibold">{formatPrice(ev.change)}</span></div> : null}
        </div>
        <DialogFooter className="gap-2 sm:gap-2">
          <Button variant="outline" className="rounded-xl" onClick={onCancel} disabled={busy} data-testid="retail-confirm-payment-no">No, go back</Button>
          <Button className="rounded-xl bg-[hsl(var(--accent))] font-semibold text-[hsl(var(--accent-foreground))] hover:bg-[hsl(var(--accent))]/90" onClick={onConfirm} disabled={busy} data-testid="retail-confirm-payment-yes">
            {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : <Check className="h-4 w-4" />}
            Yes, {method === "multi" ? "record all" : method === "eft" ? "it's EFT" : `it's ${method === "split" ? "split" : method}`}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

/**
 * Session 30.4 — Item options sheet. Asks the counter operator for sides,
 * prep choices (e.g. "How would you like it cooked?") and an optional note
 * exactly as configured on the Owner item editor, then hands a fully
 * configured line back to the cart.
 */
function ItemOptionsDialog({ item, sidesLibrary, onClose, onAdd }) {
  const open = Boolean(item);
  const cfg = item?.sides_config || {};
  const prompts = useMemo(() => prepPromptsOf(item), [item]);
  const sidesById = useMemo(() => {
    const m = {};
    (sidesLibrary || []).forEach((s) => { if (s?.id) m[String(s.id)] = s; });
    return m;
  }, [sidesLibrary]);
  const allowedSides = useMemo(
    () => (cfg.allowed_side_ids || []).map((id) => sidesById[String(id)]).filter(Boolean),
    [cfg.allowed_side_ids, sidesById],
  );
  const maxSelect = Math.max(1, Number(cfg.max_select) || 1);
  const freeCount = Math.max(0, Number(cfg.free_count) || 0);
  const sidesRequired = cfg.selection_mode === "required";

  const [sideIds, setSideIds] = useState([]);
  const [answers, setAnswers] = useState({});
  const [note, setNote] = useState("");
  const [qty, setQty] = useState(1);

  useEffect(() => {
    if (!item) return;
    const allowed = new Set((item.sides_config?.allowed_side_ids || []).map(String));
    setSideIds((item.sides_config?.included_side_ids || []).map(String).filter((id) => allowed.has(id)).slice(0, Math.max(1, Number(item.sides_config?.max_select) || 1)));
    setAnswers({});
    setNote("");
    setQty(1);
  }, [item]);

  const toggleSide = (id) => {
    setSideIds((prev) => {
      const sid = String(id);
      if (prev.includes(sid)) return prev.filter((x) => x !== sid);
      if (maxSelect === 1) return [sid];
      if (prev.length >= maxSelect) {
        toast.message(`You can pick up to ${maxSelect} side${maxSelect === 1 ? "" : "s"}.`);
        return prev;
      }
      return [...prev, sid];
    });
  };

  const missingPrompts = prompts.filter((p) => p.required !== false && !answers[p.label]);
  const sidesOk = !sidesRequired || sideIds.length > 0;
  const canAdd = sidesOk && missingPrompts.length === 0 && qty > 0;

  const { perUnit, snapshots } = computeExtras(item, sideIds, sidesLibrary);
  const unitPrice = Number(item?.price || 0) + perUnit;
  const lineTotal = Math.round(unitPrice * qty * 100) / 100;

  const submit = () => {
    if (!canAdd) {
      if (!sidesOk) toast.error("Pick at least one side.");
      else if (missingPrompts.length) toast.error(`Please answer: ${missingPrompts[0].label}`);
      return;
    }
    const prep = prompts.filter((p) => answers[p.label]).map((p) => ({ label: p.label, choice: answers[p.label] }));
    onAdd({ item, qty, sideIds, prep, note: note.trim(), extrasPerUnit: perUnit, sidesSnapshot: snapshots });
  };

  return (
    <Dialog open={open} onOpenChange={(v) => { if (!v) onClose(); }}>
      <DialogContent className="max-h-[90vh] max-w-md overflow-hidden rounded-2xl p-0" data-testid="retail-item-options-dialog">
        {item ? (
          <>
            <div className="flex items-start gap-3 border-b border-border p-5 pb-4">
              <ProductImage src={item.image} alt={item.name} className="h-16 w-16 shrink-0 rounded-xl" />
              <DialogHeader className="min-w-0 flex-1 space-y-1 text-left">
                <DialogTitle className="truncate text-base">{item.name}</DialogTitle>
                <DialogDescription className="text-xs">
                  {formatPrice(item.price)}{item.description ? ` · ${item.description}` : ""}
                </DialogDescription>
              </DialogHeader>
            </div>

            <div className="max-h-[52vh] space-y-5 overflow-y-auto p-5">
              {allowedSides.length > 0 ? (
                <section className="space-y-2" data-testid="retail-options-sides">
                  <div className="flex items-baseline justify-between gap-2">
                    <p className="text-sm font-semibold">Sides &amp; extras{sidesRequired ? <span className="text-destructive"> *</span> : null}</p>
                    <p className="text-[11px] text-muted-foreground">
                      {maxSelect === 1 ? "Pick 1" : `Pick up to ${maxSelect}`}{freeCount > 0 ? ` · first ${freeCount} free` : ""}
                    </p>
                  </div>
                  <div className="flex flex-wrap gap-2">
                    {allowedSides.map((s) => {
                      const sid = String(s.id);
                      const idx = sideIds.indexOf(sid);
                      const on = idx >= 0;
                      const charged = on ? idx >= freeCount : sideIds.length >= freeCount;
                      return (
                        <button
                          key={sid}
                          type="button"
                          onClick={() => toggleSide(sid)}
                          className={`inline-flex min-h-10 items-center gap-1.5 rounded-xl border px-3 py-2 text-sm transition-colors ${on ? "border-transparent bg-[hsl(var(--primary))] text-primary-foreground" : "border-border bg-card hover:bg-secondary"}`}
                          aria-pressed={on}
                          data-testid={`retail-side-option-${sid}`}
                        >
                          {on ? <Check className="h-3.5 w-3.5" /> : null}
                          <span>{s.name}</span>
                          {Number(s.price) > 0 ? (
                            <span className={`text-[11px] tabular-nums ${on ? "text-primary-foreground/80" : "text-muted-foreground"}`}>
                              {charged ? `+${formatPrice(s.price)}` : "free"}
                            </span>
                          ) : null}
                        </button>
                      );
                    })}
                  </div>
                </section>
              ) : null}

              {prompts.map((p) => (
                <section key={p.id || p.label} className="space-y-2" data-testid={`retail-options-prompt-${(p.id || p.label).toString().replace(/\s+/g, "-")}`}>
                  <p className="text-sm font-semibold">{p.label}{p.required !== false ? <span className="text-destructive"> *</span> : null}</p>
                  <div className="flex flex-wrap gap-2">
                    {p.options.map((opt) => {
                      const on = answers[p.label] === opt;
                      return (
                        <button
                          key={opt}
                          type="button"
                          onClick={() => setAnswers((a) => ({ ...a, [p.label]: on && p.required === false ? undefined : opt }))}
                          className={`inline-flex min-h-10 items-center gap-1.5 rounded-xl border px-3 py-2 text-sm transition-colors ${on ? "border-transparent bg-[hsl(var(--primary))] text-primary-foreground" : "border-border bg-card hover:bg-secondary"}`}
                          aria-pressed={on}
                          data-testid={`retail-prep-option-${opt.replace(/\s+/g, "-")}`}
                        >
                          {on ? <Check className="h-3.5 w-3.5" /> : null}
                          {opt}
                        </button>
                      );
                    })}
                  </div>
                </section>
              ))}

              {notesAllowed(item) ? (
                <section className="space-y-2" data-testid="retail-options-note">
                  <p className="text-sm font-semibold">Note for the kitchen <span className="font-normal text-muted-foreground">(optional)</span></p>
                  <Textarea
                    value={note}
                    onChange={(e) => setNote(e.target.value.slice(0, 200))}
                    placeholder="e.g. no onions, extra crispy"
                    className="min-h-[64px] rounded-xl"
                    data-testid="retail-options-note-input"
                  />
                </section>
              ) : null}
            </div>

            <DialogFooter className="flex-row items-center gap-3 border-t border-border p-4 sm:justify-between">
              <div className="flex items-center gap-1">
                <Button variant="outline" size="icon" className="h-10 w-10 rounded-xl" onClick={() => setQty((q) => Math.max(1, q - 1))} data-testid="retail-options-qty-minus">
                  <Minus className="h-4 w-4" />
                </Button>
                <span className="w-8 text-center text-sm font-bold tabular-nums" data-testid="retail-options-qty">{qty}</span>
                <Button variant="outline" size="icon" className="h-10 w-10 rounded-xl" onClick={() => setQty((q) => Math.min(200, q + 1))} data-testid="retail-options-qty-plus">
                  <Plus className="h-4 w-4" />
                </Button>
              </div>
              <Button
                className="h-11 flex-1 rounded-xl bg-[hsl(var(--accent))] font-semibold text-[hsl(var(--accent-foreground))] hover:bg-[hsl(var(--accent))]/90 disabled:opacity-60"
                disabled={!canAdd}
                onClick={submit}
                data-testid="retail-options-add-button"
              >
                Add · {formatPrice(lineTotal)}
              </Button>
            </DialogFooter>
          </>
        ) : null}
      </DialogContent>
    </Dialog>
  );
}

/** Tiny hook: live WS connection status for the header badge. */
const useRealtimeStatus = () => {
  const [connected, setConnected] = useState(() => {
    try { return Boolean(realtime._connected); } catch (_) { return false; }
  });
  useEffect(() => {
    try { return realtime.onStatus((v) => setConnected(Boolean(v))); }
    catch (_) { return undefined; }
  }, []);
  return connected;
};

export default function RetailDashboard({ session, onLogout, theme, onToggleTheme }) {
  const token = session?.token;
  const business = session?.business || {};
  const userLabel =
    session?.user
      ? `${session.user.first_name || ""} ${session.user.last_name || ""}`.trim() || "Retail"
      : "Retail";

  const http = useMemo(() => axios.create({
    baseURL: `${BACKEND_URL}/api`,
    timeout: 20000,
    headers: token ? { Authorization: `Bearer ${token}` } : {},
  }), [token]);

  // ── Catalog state
  const [catalog, setCatalog] = useState(null);
  const [loadingCatalog, setLoadingCatalog] = useState(true);
  const [search, setSearch] = useState("");
  const [activeCat, setActiveCat] = useState("all");

  // ── Ticket / payment state machine: "cart" → "pay" → "done"
  // Each line: {key, id, name, price, qty, department, selected_side_ids,
  // prep_selections, note, extras_per_unit, sides_snapshot}
  const [cart, setCart] = useState([]);
  const [optionsItem, setOptionsItem] = useState(null); // item awaiting sides/prep choices
  const [stage, setStage] = useState("cart");
  const [salePay, setSalePay] = useState(EMPTY_PAY); // Session 30.16 strict capture
  const [confirmPay, setConfirmPay] = useState({ open: false }); // "Are you sure?" gate
  const [busy, setBusy] = useState(false);
  const [lastSale, setLastSale] = useState(null);

  // ── Recent sales / void / prep feed
  const [recentOpen, setRecentOpen] = useState(false);
  const [recentSales, setRecentSales] = useState([]);
  const [loadingRecent, setLoadingRecent] = useState(false);
  const [voidDialog, setVoidDialog] = useState({ open: false, sale: null, pin: "", busy: false });
  const [printerOpen, setPrinterOpen] = useState(false); // Session 30.7 printer setup dialog

  // ── Session 30.5 — Tabs (open table sessions shared with Staff/Owner)
  const [tabs, setTabs] = useState([]);
  const [loadingTabs, setLoadingTabs] = useState(false);
  const [tabsOpen, setTabsOpen] = useState(false);
  const [activeTab, setActiveTab] = useState(null); // session the cart will be added to
  const [newTabName, setNewTabName] = useState("");
  const [tabSearch, setTabSearch] = useState("");
  // Session 30.30 — "all" | "online": online = tabs with digital-menu
  // (QR) orders, so staff can see which cash orders still need paying.
  const [tabFilter, setTabFilter] = useState("all");
  const [openingTab, setOpeningTab] = useState(false);
  const [settle, setSettle] = useState({ open: false, session: null, pay: EMPTY_PAY, busy: false });

  const connected = useRealtimeStatus();
  const vatRate = Number(catalog?.business?.vat_rate ?? business?.vat_rate ?? 15);

  // ── Data fetchers ────────────────────────────────────────────────
  const fetchTabs = useCallback(async (silent = true) => {
    if (!silent) setLoadingTabs(true);
    try {
      const r = await http.get("/staff/table-sessions", { params: { status: "open" } });
      const list = Array.isArray(r.data?.table_sessions) ? r.data.table_sessions : [];
      // Retail one-shot sales use channel="retail" and are already filtered
      // server-side; everything else is a tab/table shared with Staff.
      list.sort((a, b) => String(b.last_activity_at || b.updated_at || "").localeCompare(String(a.last_activity_at || a.updated_at || "")));
      // Session 30.34 — Keep tabs that were opened offline (local-… ids)
      // and have not yet been confirmed by the server: the sync loop
      // may still be draining. They fall away once the server copy
      // appears (same table name) or after 3 minutes.
      setTabs((prev) => {
        const serverNames = new Set(list.map((s) => String(s.table_number || "").toLowerCase()));
        const keepLocal = (Array.isArray(prev) ? prev : []).filter((t) =>
          t && String(t.id || "").startsWith("local-")
          && !serverNames.has(String(t.table_number || "").toLowerCase())
          && (Date.now() - new Date(t.created_at || 0).getTime()) < 3 * 60 * 1000,
        );
        return keepLocal.length ? [...keepLocal, ...list] : list;
      });
      setActiveTab((cur) => {
        if (!cur) return cur;
        if (String(cur.id || "").startsWith("local-")) {
          // Re-point at the server copy once it exists (same name).
          const byName = list.find((s) => String(s.table_number || "").toLowerCase() === String(cur.table_number || "").toLowerCase());
          return byName || cur;
        }
        const fresh = list.find((s) => s.id === cur.id);
        return fresh || null; // tab closed elsewhere → drop the target
      });
    } catch (e) {
      if (e?.response?.status === 401) { onLogout?.(); return; }
      if (!silent) toast.error("Could not load open tabs.");
    } finally {
      if (!silent) setLoadingTabs(false);
    }
  }, [http, onLogout]);

  // Session 31.7 — Customer orders held for approval ("Have staff confirm
  // order before departments"). Same Accept/Decline as the Staff dashboard.
  const [pendingApprovals, setPendingApprovals] = useState([]);
  const [approvalBusyId, setApprovalBusyId] = useState(null);
  const handledApprovalsRef = useRef(new Map()); // id → ts; ignore in polls for 15 s
  const knownApprovalIdsRef = useRef(null);       // Session 31.8 — chime only for NEW held orders
  const approvalsCardRef = useRef(null);
  const fetchPendingApprovals = useCallback(async () => {
    try {
      const r = await http.get("/staff/pending-confirmations");
      const nowTs = Date.now();
      for (const [id, ts] of handledApprovalsRef.current) { if (nowTs - ts > 15000) handledApprovalsRef.current.delete(id); }
      const rows = (Array.isArray(r.data?.orders) ? r.data.orders : []).filter((o) => !handledApprovalsRef.current.has(o.id));
      const ids = new Set(rows.map((o) => o.id));
      if (knownApprovalIdsRef.current === null) {
        knownApprovalIdsRef.current = ids; // first load: no chime for what was already waiting
      } else {
        const fresh = rows.filter((o) => !knownApprovalIdsRef.current.has(o.id));
        knownApprovalIdsRef.current = ids;
        if (fresh.length) {
          // Same distinct "order awaits approval" chime as the Staff dashboard.
          try {
            if (typeof window.__nfcsPlayStaffConfirmationChime === "function") window.__nfcsPlayStaffConfirmationChime();
            else playOrderReadyChime();
          } catch (_) { /* audio blocked until first tap */ }
          toast.info(fresh.length === 1
            ? `New order from Table ${fresh[0].table_number || "—"} needs your approval`
            : `${fresh.length} new orders need your approval`);
        }
      }
      setPendingApprovals(rows);
    } catch (_) { /* gate OFF or offline → card simply hidden */ }
  }, [http]);
  const acceptPendingApproval = useCallback(async (orderId) => {
    setApprovalBusyId(orderId);
    handledApprovalsRef.current.set(orderId, Date.now());
    setPendingApprovals((prev) => prev.filter((o) => o.id !== orderId));
    try {
      await http.post(`/staff/orders/${orderId}/confirm`, {});
      toast.success("Order accepted — sent to kitchen & bar");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not accept the order.");
      handledApprovalsRef.current.delete(orderId);
      fetchPendingApprovals();
    } finally {
      setApprovalBusyId(null);
      fetchTabs(true);
    }
  }, [http, fetchPendingApprovals, fetchTabs]);
  const declinePendingApproval = useCallback(async (orderId, reason = null) => {
    setApprovalBusyId(orderId);
    handledApprovalsRef.current.set(orderId, Date.now());
    setPendingApprovals((prev) => prev.filter((o) => o.id !== orderId));
    try {
      await http.post(`/staff/orders/${orderId}/reject`, { reason: reason || null });
      toast.success("Order declined");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not decline the order.");
      handledApprovalsRef.current.delete(orderId);
      fetchPendingApprovals();
    } finally {
      setApprovalBusyId(null);
      fetchTabs(true);
    }
  }, [http, fetchPendingApprovals, fetchTabs]);

  const tabsFetchTimer = useRef(null);
  const scheduleTabsRefresh = useCallback(() => {
    if (tabsFetchTimer.current) return;
    tabsFetchTimer.current = setTimeout(() => {
      tabsFetchTimer.current = null;
      fetchTabs(true);
    }, 600);
  }, [fetchTabs]);
  // Session 30.17 — Quick Keys: top sellers pinned above the grid.
  const [bestSellerIds, setBestSellerIds] = useState([]);
  const fetchBestSellers = useCallback(async () => {
    try {
      const r = await http.get("/retail/best-sellers", { params: { days: 30, limit: 12 } });
      let rows = Array.isArray(r.data?.items) ? r.data.items : [];
      if (rows.length < 4) {
        // Quiet business → widen the window so the row is never empty.
        const r2 = await http.get("/retail/best-sellers", { params: { days: 365, limit: 12 } });
        rows = Array.isArray(r2.data?.items) ? r2.data.items : rows;
      }
      setBestSellerIds(rows.map((x) => String(x.menu_item_id)));
    } catch (_) { /* quick keys are a convenience — fail silently */ }
  }, [http]);

  const fetchCatalog = useCallback(async () => {
    try {
      const r = await http.get("/retail/catalog");
      setCatalog(r.data || null);
    } catch (e) {
      if (e?.response?.status === 401) { onLogout?.(); return; }
      toast.error("Could not load the menu — retrying is safe.");
    } finally {
      setLoadingCatalog(false);
    }
  }, [http, onLogout]);

  const salesFetchTimer = useRef(null);
  const fetchRecentSales = useCallback(async (silent = true) => {
    if (!silent) setLoadingRecent(true);
    try {
      const r = await http.get("/retail/sales", { params: { limit: 30 } });
      setRecentSales(Array.isArray(r.data?.sales) ? r.data.sales : []);
    } catch (e) {
      if (e?.response?.status === 401) { onLogout?.(); return; }
      if (!silent) toast.error("Could not load recent sales.");
    } finally {
      if (!silent) setLoadingRecent(false);
    }
  }, [http, onLogout]);

  const scheduleSalesRefresh = useCallback(() => {
    if (salesFetchTimer.current) return;
    salesFetchTimer.current = setTimeout(() => {
      salesFetchTimer.current = null;
      fetchRecentSales(true);
    }, 600);
  }, [fetchRecentSales]);

  useEffect(() => {
    fetchCatalog();
    fetchRecentSales(true);
    fetchTabs(true);
    fetchBestSellers();
    fetchPendingApprovals();
    const t = setInterval(() => { fetchRecentSales(true); fetchTabs(true); }, 30000); // safety net
    const ta = setInterval(fetchPendingApprovals, 5000); // held customer orders
    const tb = setInterval(fetchBestSellers, 10 * 60 * 1000);
    // Session 58 — settings/catalog safety net (VAT, service fee, prices)
    // in case the realtime event was missed while the socket was down.
    const tc = setInterval(fetchCatalog, 2 * 60 * 1000);
    return () => { clearInterval(t); clearInterval(ta); clearInterval(tb); clearInterval(tc); };
  }, [fetchCatalog, fetchRecentSales, fetchTabs, fetchBestSellers, fetchPendingApprovals]);

  // ── Realtime: prep status for sales routed to kitchen/bar + tabs ──
  useRealtimeConnection({ baseUrl: BACKEND_URL, token });
  useRealtimeEvent(["hello", "order_status_changed", "order_created", "table_session_opened", "table_session_closed", "payment_recorded", "business_settings_changed", "menu_item_updated", "menu_item_created", "menu_item_deleted", "category_updated", "category_created", "category_deleted"], (msg) => {
    const type = msg?.event_type || msg?.type;
    if (type === "hello") { scheduleSalesRefresh(); scheduleTabsRefresh(); fetchPendingApprovals(); return; }
    // Session 58 — Menu Settings (VAT / service fee) or the menu itself
    // changed: re-pull the catalog so this till charges the right numbers.
    if (type === "business_settings_changed" || String(type || "").startsWith("menu_item_") || String(type || "").startsWith("category_")) { fetchCatalog(); return; }
    if (type === "order_created" || type === "order_status_changed") fetchPendingApprovals();
    const p = msg?.payload || {};
    const tn = String(p.table_number || "");
    if (tn.startsWith("Retail #")) { scheduleSalesRefresh(); return; }
    scheduleTabsRefresh();
  });

  // ── Derived catalog view ─────────────────────────────────────────
  const categories = useMemo(() => {
    const cats = (catalog?.categories || []).filter((c) => c.active !== false);
    return cats.filter((c) => (c.items || []).some((it) => it.active !== false));
  }, [catalog]);

  // Session 31.19 — Quick keys can be switched off per device (some tills
  // prefer the full grid). Remembered in localStorage.
  const QUICK_KEYS_KEY = "nfcs:retail:quick-keys";
  const [quickKeysOn, setQuickKeysOn] = useState(() => {
    try { return localStorage.getItem(QUICK_KEYS_KEY) !== "0"; } catch (_) { return true; }
  });
  const toggleQuickKeys = (on) => {
    setQuickKeysOn(!!on);
    try { localStorage.setItem(QUICK_KEYS_KEY, on ? "1" : "0"); } catch (_) { /* noop */ }
  };
  // Session 31.20 — on phones the ticket panel can't sit beside the
  // catalog, so it opens as a full-screen sheet from a pinned bottom bar.
  const [mobileTicketOpen, setMobileTicketOpen] = useState(false);
  useEffect(() => { if (stage === "done") setMobileTicketOpen(true); }, [stage]);

  const quickKeys = useMemo(() => {
    const all = [];
    const seen = new Set();
    categories.forEach((c) => (c.items || []).forEach((it) => {
      if (it.active === false || seen.has(it.id)) return;
      seen.add(it.id);
      all.push({ ...it, _cat: c.name });
    }));
    const byId = new Map(all.map((it) => [String(it.id), it]));
    const ranked = bestSellerIds.map((id) => byId.get(id)).filter(Boolean);
    return (ranked.length ? ranked : all).slice(0, 8);
  }, [categories, bestSellerIds]);

  // Session 31.23 — items are ALWAYS alphabetical within their category
  // (owner request, same rule as the staff "Add items" grid). "All" keeps
  // the category order and sorts inside each; search sorts the matches.
  const azSort = (list) => [...list].sort((a, b) => String(a?.name || "").localeCompare(String(b?.name || ""), undefined, { sensitivity: "base" }));
  const visibleItems = useMemo(() => {
    const q = search.trim().toLowerCase();
    let items;
    if (q) {
      items = azSort(categories.flatMap((c) => (c.items || []).map((it) => ({ ...it, _cat: c.name }))));
    } else if (activeCat === "all") {
      items = categories.flatMap((c) => azSort(c.items || []).map((it) => ({ ...it, _cat: c.name })));
    } else {
      const cat = categories.find((c) => c.id === activeCat);
      items = azSort(cat?.items || []).map((it) => ({ ...it, _cat: cat?.name }));
    }
    items = items.filter((it) => it.active !== false);
    if (q) items = items.filter((it) => `${it.name} ${it.description || ""}`.toLowerCase().includes(q));
    // De-duplicate: specials can surface in BOTH the specials category
    // and their base category — the same id twice breaks React keys.
    const seen = new Set();
    return items.filter((it) => {
      if (seen.has(it.id)) return false;
      seen.add(it.id);
      return true;
    });
  }, [categories, activeCat, search]);

  // ── Cart maths ───────────────────────────────────────────────────
  const lineUnitPrice = (l) => Number(l.price || 0) + Number(l.extras_per_unit || 0);
  const cartTotal = useMemo(
    () => Math.round(cart.reduce((sum, l) => sum + lineUnitPrice(l) * l.qty, 0) * 100) / 100,
    [cart],
  );
  const cartCount = useMemo(() => cart.reduce((n, l) => n + l.qty, 0), [cart]);
  const qtyByItem = useMemo(() => {
    const m = {};
    cart.forEach((l) => { m[l.id] = (m[l.id] || 0) + l.qty; });
    return m;
  }, [cart]);
  const vatPortion = useMemo(
    () => (vatRate > 0 ? Math.round((cartTotal * vatRate / (100 + vatRate)) * 100) / 100 : 0),
    [cartTotal, vatRate],
  );
  // Session 47 — automatic service fee on top of the items total (same
  // rule as table bills; mirrors backend compute_service_fee).
  const feeBiz = catalog?.business || business || {};
  const serviceFeePct = feeBiz.service_fee_enabled ? (Number(feeBiz.service_fee_pct) || 0) : 0;
  const serviceFee = serviceFeePct > 0 && cartTotal > 0 ? Math.round(cartTotal * serviceFeePct) / 100 : 0;
  const chargeTotal = Math.round((cartTotal + serviceFee) * 100) / 100;

  const pushLine = ({ item, qty = 1, sideIds = [], prep = [], note = "", extrasPerUnit = 0, sidesSnapshot = [] }) => {
    const key = buildLineKey(item.id, sideIds, prep, note);
    setCart((prev) => {
      const existing = prev.find((l) => l.key === key);
      if (existing) return prev.map((l) => (l.key === key ? { ...l, qty: l.qty + qty } : l));
      return [...prev, {
        key,
        id: item.id,
        name: item.name,
        price: Number(item.price || 0),
        qty,
        department: item.department || "",
        selected_side_ids: sideIds.map(String),
        prep_selections: prep,
        note: note || "",
        extras_per_unit: Number(extrasPerUnit || 0),
        sides_snapshot: sidesSnapshot,
      }];
    });
  };

  const addToCart = (item) => {
    if (item?.sellable === false) { toast.error(`${item.name} is out of stock.`); return; }
    if (needsOptions(item)) {
      setOptionsItem(item);
      return;
    }
    pushLine({ item });
  };
  const changeQty = (key, delta) => {
    setCart((prev) => prev
      .map((l) => (l.key === key ? { ...l, qty: l.qty + delta } : l))
      .filter((l) => l.qty > 0));
  };
  const removeLine = (key) => setCart((prev) => prev.filter((l) => l.key !== key));
  const clearCart = () => { setCart([]); setStage("cart"); setSalePay(EMPTY_PAY); };

  /** Human summary of a line's chosen options for the ticket + receipt. */
  const lineOptionsSummary = (l) => {
    const parts = [];
    (l.sides_snapshot || []).forEach((s) => parts.push(s.charged && s.price > 0 ? `${s.name} (+${formatPrice(s.price)})` : s.name));
    (l.prep_selections || []).forEach((p) => parts.push(`${p.label}: ${p.choice}`));
    if (l.note) parts.push(`"${l.note}"`);
    return parts.join(" · ");
  };

  // ── Payment helpers (Session 30.16 — strict typed amounts) ──────
  const saleEval = evaluatePayment(salePay, chargeTotal);
  const canComplete = !busy && cart.length > 0 && saleEval.valid;

  /** Step 1: operator taps Complete → "Are you sure?" */
  const askCompleteSale = () => {
    if (!canComplete) { toast.error(saleEval.reason || "Enter the payment amount first."); return; }
    setConfirmPay({ open: true, kind: "sale", method: salePay.method, total: chargeTotal, ev: saleEval, label: "this sale" });
  };

  /** Step 2: confirmed → charge. */
  const completeSale = async () => {
    if (!cart.length || !saleEval.valid) return;
    const payMethod = salePay.method;
    const tender = saleEval.tendered || 0;
    const splitCash = saleEval.cash;
    const splitCard = saleEval.card;
    const splitEft = saleEval.eft || 0;
    // Session 31.26 — every method resolves to a tender list (cash /
    // several cards / EFT, each with its own tip) so the offline path and
    // the "multi" API payload share one shape.
    const tenderList = (saleEval.payments || []).map((t) => ({ ...t, id: `t-${Date.now().toString(36)}-${Math.random().toString(36).slice(2, 8)}` }));
    setBusy(true);
    try {
      const body = {
        items: cart.map((l) => ({
          menu_item_id: l.id,
          quantity: l.qty,
          selected_side_ids: l.selected_side_ids || [],
          prep_selections: l.prep_selections || [],
          ...(l.note ? { note: l.note } : {}),
        })),
        payment_method: payMethod,
      };
      if (payMethod === "cash" && tender > 0) body.tender_amount = tender;
      if (payMethod === "split") {
        body.cash_amount = splitCash;
        body.card_amount = splitCard;
        if (tender > 0) body.tender_amount = tender;
      }
      if (payMethod === "multi") {
        body.payments = tenderList.map((t) => ({ id: t.id, method: t.method, amount: t.amount, tip: t.tip || 0 }));
        body.tip_amount = saleEval.tip || 0;
      }
      let sale = null;
      try {
        const r = await http.post("/retail/sales", body);
        sale = r.data?.sale;
      } catch (e) {
        if (!isNetErr(e)) throw e;
        // Offline quick sale: dedicated retail session + order + payment,
        // exactly what /retail/sales does server-side. Kitchen/bar items
        // still reach the stations over LAN; the session auto-closes on
        // the server once the runner delivers (or immediately if nothing
        // needed preparing).
        const lines = enrichCartLines();
        const saleNo = `Retail #${new Date().toISOString().slice(11, 19).replace(/:/g, "")}`;
        const sess = await openTabOffline(saleNo, { channel: "retail", retail_sale: true });
        setTabs((prev) => (prev || []).filter((t) => t && t.id !== sess.id)); // never listed as a tab
        const order = await writeOrderOffline(sess, lines);
        setTabs((prev) => (prev || []).filter((t) => t && t.id !== sess.id));
        const offlineFee = serviceFeePct > 0 ? Math.round(Number(order.total || 0) * serviceFeePct) / 100 : 0;
        const total = Math.round((Number(order.total || 0) + offlineFee) * 100) / 100;
        const cashAmt = payMethod === "cash" ? total : payMethod === "card" || payMethod === "eft" ? 0 : splitCash;
        const cardAmt = payMethod === "card" ? total : payMethod === "cash" || payMethod === "eft" ? 0 : splitCard;
        const eftAmt = payMethod === "eft" ? total : payMethod === "multi" ? splitEft : 0;
        // Session 31.26 — append the tenders to the offline ledger (same
        // reducer the Staff dashboard uses); the server-side
        // `payment_added` reducer replays it against Mongo.
        await localWrite({
          type: EventType.PAYMENT_ADDED,
          session,
          payload: { table_session_id: sess.id, payments: tenderList.length ? tenderList : [{ method: payMethod === "eft" ? "eft" : payMethod === "card" ? "card" : "cash", amount: total, tip: 0 }], recorded_at: new Date().toISOString() },
        });
        const prep = lines.filter((l) => ["kitchen", "bar"].includes(String(l.department || "").toLowerCase())).reduce((n, l) => n + Number(l.quantity || 0), 0);
        if (!prep) {
          await localWrite({ type: EventType.TABLE_CLOSED, session, payload: { table_session_id: sess.id, table_session: { id: sess.id, table_number: sess.table_number }, ts: new Date().toISOString() } });
        }
        sale = {
          session_id: sess.id, table_number: saleNo, total, subtotal: total, items_total: order.total, service_fee_pct: offlineFee > 0 ? serviceFeePct : null, service_fee_amount: offlineFee,
          cash_received: cashAmt, card_received: cardAmt, eft_received: eftAmt, payments: tenderList, tip_amount: saleEval.tip || 0, payment_method: payMethod,
          change_due: payMethod === "cash" && tender > total ? Math.round((tender - total) * 100) / 100 : 0,
          created_at: order.created_at, sold_by: userLabel, order, kitchen_bar_items: prep, _offline: true,
        };
        toast.message("Offline — sale saved on this device and shared over LAN; it syncs when internet returns.");
      }
      setLastSale(sale || null);
      setStage("done");
      setCart([]);
      setSalePay(EMPTY_PAY);
      setConfirmPay({ open: false });
      if (!sale?._offline) fetchRecentSales(true);
      if (sale?.kitchen_bar_items > 0) {
        toast.success(`${sale.table_number} sent to ${sale.kitchen_bar_items === 1 ? "the station" : "kitchen/bar"}`, {
          description: "It will appear on the prep strip until the runner delivers it.",
        });
      } else {
        toast.success("Sale completed");
      }
    } catch (e) {
      if (e?.response?.status === 401) { onLogout?.(); return; }
      // Session 41 — structured fallback-policy refusal (OUT OF STOCK AT
      // SERVICE LOCATION / MANAGER APPROVAL REQUIRED): nothing was sold.
      const d = e?.response?.data?.detail;
      if (d && typeof d === "object" && d.code && Array.isArray(d.blocked)) {
        const who = d.blocked.map((b) => `${b.ingredient_name} (need ${b.required} ${b.unit}, have ${b.preferred_balance})`).join("; ");
        toast.error(d.message || "Cannot sell this right now", { description: `${who}. ${d.hint || ""} Nothing was charged.` });
        return;
      }
      toast.error(errDetail(e, "Sale failed — nothing was charged."));
    } finally {
      setBusy(false);
    }
  };

  // ── Session 30.5 — Tabs: open / add-to / settle ──────────────────
  const cartToOrderItems = () => cart.map((l) => ({
    menu_item_id: l.id,
    quantity: l.qty,
    selected_side_ids: l.selected_side_ids || [],
    prep_selections: l.prep_selections || [],
    ...(l.note ? { note: l.note } : {}),
  }));

  const errDetail = (e, fallback) => {
    const d = e?.response?.data?.detail;
    if (typeof d === "string") return d;
    if (d && typeof d === "object" && d.message) return d.message;
    return fallback;
  };

  // ── Session 30.34 — LAN / offline fallbacks ─────────────────────────
  // The Retail dashboard used plain HTTP for every action, so on a
  // router-only network (no internet) opening a tab, adding items,
  // settling or ringing a quick sale simply failed. Mirror the Staff
  // dashboard: when the cloud is unreachable (or the tab only exists
  // locally) write the same events the offline reducers understand —
  // TABLE_OPENED / ORDER_CREATED / PAYMENT_RECORDED / TABLE_CLOSED. The
  // LAN-aware localWrite mirrors each event to Kitchen / Bar / Staff /
  // Runner peers instantly and syncs to the cloud when internet returns.
  const isNetErr = (e) => !e?.response;
  const isLocalId = (id) => String(id || "").startsWith("local-");
  const newLocalId = () => `local-${(crypto && crypto.randomUUID) ? crypto.randomUUID() : `${Date.now()}-${Math.random().toString(16).slice(2)}`}`;
  const catalogLookup = useMemo(() => {
    const m = new Map();
    (catalog?.categories || []).forEach((c) => (c.items || []).forEach((it) => m.set(it.id, { ...it, category_name: c.name })));
    return m;
  }, [catalog]);
  const enrichCartLines = () => cart.map((l) => {
    const m = catalogLookup.get(l.id) || {};
    const unit = Number(l.price ?? m.price ?? 0);
    return {
      menu_item_id: l.id,
      quantity: l.qty,
      selected_side_ids: l.selected_side_ids || [],
      prep_selections: l.prep_selections || [],
      ...(l.note ? { note: l.note } : {}),
      name: l.name || m.name || "",
      department: l.department || m.department || "",
      price: unit,
      line_total: Math.round(unit * l.qty * 100) / 100,
    };
  });
  const buildLocalOrder = (sess, items) => {
    const total = Math.round(items.reduce((n, it) => n + Number(it.line_total || 0), 0) * 100) / 100;
    const nowIso = new Date().toISOString();
    return {
      id: newLocalId(),
      table_session_id: sess.id,
      table_number: sess.table_number,
      source: "staff",
      status: "confirmed",
      created_at: nowIso,
      updated_at: nowIso,
      items: items.map((it) => ({ ...it, status: "pending", _pending: true })),
      total, subtotal: total, vat_total: 0,
      total_items: items.reduce((n, it) => n + Number(it.quantity || 0), 0),
      _pending_sync: true, _local_only: true,
    };
  };
  const writeOrderOffline = async (sess, items) => {
    const order = buildLocalOrder(sess, items);
    await localWrite({
      type: EventType.ORDER_CREATED,
      session,
      payload: {
        order,
        table_session: { id: sess.id, table_number: sess.table_number, status: "open", channel: sess.channel || undefined, updated_at: order.created_at },
      },
    });
    // Optimistic tabs-state patch so the Tabs list is right immediately.
    setTabs((prev) => {
      const list = Array.isArray(prev) ? prev.slice() : [];
      const idx = list.findIndex((t) => t && t.id === sess.id);
      const base = idx >= 0 ? list[idx] : { ...sess, orders: [], order_count: 0, total_value: 0, status: "open" };
      const orders = [...(base.orders || []), order];
      const patched = {
        ...base, orders,
        order_count: orders.length,
        total_value: Math.round((Number(base.total_value || 0) + order.total) * 100) / 100,
        pending_orders: Number(base.pending_orders || 0) + 1,
        _pending_sync: true,
      };
      if (idx >= 0) list[idx] = patched; else list.unshift(patched);
      return list;
    });
    return order;
  };
  const openTabOffline = async (name, extra = {}) => {
    const nowIso = new Date().toISOString();
    const sess = {
      id: newLocalId(),
      table_number: name,
      status: "open",
      created_at: nowIso, updated_at: nowIso, opened_at: nowIso,
      assigned_user_id: session?.user?.id || null,
      assigned_user_name: userLabel,
      assigned_user_role: "retail",
      orders: [], order_count: 0, total_value: 0,
      _pending_sync: true, _local_only: true,
      ...extra,
    };
    await localWrite({ type: EventType.TABLE_OPENED, payload: { table_session: sess }, session });
    setTabs((prev) => [sess, ...(Array.isArray(prev) ? prev : [])]);
    return sess;
  };
  const settleOffline = async (sess, tenders, total) => {
    const nowIso = new Date().toISOString();
    if (total > 0 && Array.isArray(tenders) && tenders.length) {
      // Session 31.26 — multi-tender ledger append (cash / cards / EFT).
      await localWrite({
        type: EventType.PAYMENT_ADDED,
        session,
        payload: { table_session_id: sess.id, payments: tenders, recorded_at: nowIso },
      });
    }
    await localWrite({
      type: EventType.TABLE_CLOSED,
      session,
      payload: { table_session_id: sess.id, table_session: { id: sess.id, table_number: sess.table_number }, ts: nowIso },
    });
    setTabs((prev) => (Array.isArray(prev) ? prev : []).filter((t) => t && t.id !== sess.id));
  };
  // Keep the Tabs list in step with events that arrive from OTHER devices
  // over the LAN (a waiter closes a tab on the Staff phone, the kitchen
  // marks a ticket ready…) — without this, offline Retail only ever saw
  // its own changes until the next successful HTTP poll.
  useEffect(() => {
    const onLocal = (ev) => {
      const env = ev?.detail;
      const t = env?.event_type || env?.type;
      const pl = env?.payload || {};
      if (t === "table_closed") {
        const sid = pl.table_session_id || pl.table_session?.id;
        if (sid) setTabs((prev) => (Array.isArray(prev) ? prev : []).filter((x) => x && x.id !== sid));
      }
      if (t === "table_opened" && pl.table_session?.id) {
        const ts = pl.table_session;
        if (String(ts.channel || "") === "retail") return; // quick sales never show as tabs
        setTabs((prev) => (prev || []).some((x) => x && x.id === ts.id) ? prev : [{ ...ts, orders: ts.orders || [], order_count: 0, total_value: 0 }, ...(prev || [])]);
      }
      if (t === "order_created" && pl.order?.table_session_id) {
        const o = pl.order;
        setTabs((prev) => (prev || []).map((x) => {
          if (!x || x.id !== o.table_session_id) return x;
          if ((x.orders || []).some((k) => k.id === o.id)) return x;
          const orders = [...(x.orders || []), o];
          return { ...x, orders, order_count: orders.length, total_value: Math.round((Number(x.total_value || 0) + Number(o.total || 0)) * 100) / 100 };
        }));
      }
    };
    window.addEventListener(LOCAL_EVENT_APPLIED, onLocal);
    return () => window.removeEventListener(LOCAL_EVENT_APPLIED, onLocal);
  }, []);

  /** Open a named tab. If the cart has items they go straight onto it. */
  const openTab = async () => {
    const name = newTabName.trim();
    if (!name) { toast.error("Give the tab a name (e.g. a customer's name)."); return; }
    setOpeningTab(true);
    try {
      let sess = null;
      let offline = false;
      try {
        const r = await http.post("/staff/table-sessions", { table_number: name.slice(0, 20) });
        sess = r.data?.table_session;
        if (!sess) throw new Error("No session returned");
      } catch (e) {
        if (!isNetErr(e)) throw e;
        sess = await openTabOffline(name.slice(0, 20));
        offline = true;
      }
      let added = 0;
      if (cart.length) {
        if (offline || isLocalId(sess.id)) {
          await writeOrderOffline(sess, enrichCartLines());
        } else {
          try {
            await http.post(`/staff/table-sessions/${sess.id}/items`, { items: cartToOrderItems() });
          } catch (e) {
            if (!isNetErr(e)) throw e;
            await writeOrderOffline(sess, enrichCartLines());
            offline = true;
          }
        }
        added = cartCount;
        setCart([]);
        setStage("cart");
      }
      setNewTabName("");
      setActiveTab(sess);
      setTabsOpen(false);
      if (!offline) await fetchTabs(true);
      toast.success(`Tab "${sess.table_number}" opened${added ? ` · ${added} item${added === 1 ? "" : "s"} added` : ""}`, {
        description: offline ? "Offline — saved on this device, shared over LAN, syncs when internet returns." : "Tap products to keep adding to this tab, or settle it from Tabs.",
      });
    } catch (e) {
      if (e?.response?.status === 401) { onLogout?.(); return; }
      toast.error(errDetail(e, "Could not open the tab."));
    } finally {
      setOpeningTab(false);
    }
  };

  /** Send the current cart to the active tab (kitchen/bar routing as usual). */
  const addCartToTab = async () => {
    if (!activeTab || !cart.length) return;
    setBusy(true);
    try {
      let offline = false;
      if (isLocalId(activeTab.id)) {
        await writeOrderOffline(activeTab, enrichCartLines());
        offline = true;
      } else {
        try {
          await http.post(`/staff/table-sessions/${activeTab.id}/items`, { items: cartToOrderItems() });
        } catch (e) {
          if (!isNetErr(e)) throw e;
          await writeOrderOffline(activeTab, enrichCartLines());
          offline = true;
        }
      }
      const n = cartCount;
      setCart([]);
      setStage("cart");
      if (!offline) await fetchTabs(true);
      toast.success(`${n} item${n === 1 ? "" : "s"} added to ${tabLabel(activeTab)}`, {
        description: offline ? "Offline — sent to kitchen/bar over LAN; syncs when internet returns." : undefined,
      });
    } catch (e) {
      if (e?.response?.status === 401) { onLogout?.(); return; }
      toast.error(errDetail(e, "Could not add items to the tab."));
    } finally {
      setBusy(false);
    }
  };

  /** Staff tables are stored as bare numbers — show them as "Table 5". */
  const tabLabel = (s) => { const n = String(s?.table_number || "").trim(); return /^\d+$/.test(n) ? `Table ${n}` : n; };
  const tabTotal = (s) => Math.round(Number(s?.total_value || 0) * 100) / 100;
  // Session 36 — FINANCIAL INTEGRITY: settle what the server says is STILL
  // OWED (after vouchers, incl. service fee, minus tenders already on the
  // ledger) — never the raw item total. Settling against `total_value`
  // under-paid fee-bearing tabs (server then refused the close) and
  // over-paid voucher'd ones.
  const tabOutstanding = (s) => {
    const v = s?.outstanding_amount ?? s?.amount_owed ?? s?.total_value;
    return Math.round(Number(v || 0) * 100) / 100;
  };
  const tabItems = (s) => (s?.orders || []).flatMap((o) => (o.items || []).filter((it) => !it.voided));
  const tabItemCount = (s) => tabItems(s).reduce((n, it) => n + Number(it.quantity || 0), 0);

  // Search across the tab/table name ("5" matches "Table 5" and "5"), the
  // assigned waiter, the customer name and the items on the tab.
  const onlineTabs = useMemo(() => tabs.filter((s) => Number(s.online_orders || 0) > 0), [tabs]);
  const cashDueTabs = useMemo(() => tabs.filter((s) => Number(s.online_cash_due || 0) > 0), [tabs]);
  const cashDueTotal = useMemo(() => Math.round(cashDueTabs.reduce((n, s) => n + Number(s.online_cash_due || 0), 0) * 100) / 100, [cashDueTabs]);
  const filteredTabs = useMemo(() => {
    const q = tabSearch.trim().toLowerCase();
    const base = tabFilter === "online" ? tabs.filter((s) => Number(s.online_orders || 0) > 0) : tabs;
    if (!q) return base;
    return base.filter((s) => {
      const items = (s.orders || []).flatMap((o) => (o.items || []).map((it) => it.name || ""));
      const hay = `${s.table_number || ""} table ${s.table_number || ""} ${s.assigned_user_name || ""} ${s.customer_name || ""} ${s.customer_phone || ""} ${(s.online_order_numbers || []).join(" ")} ${items.join(" ")}`.toLowerCase();
      return hay.includes(q);
    });
  }, [tabs, tabSearch, tabFilter]);

  const openSettle = (sess) => setSettle({ open: true, session: sess, pay: EMPTY_PAY, busy: false });
  const closeSettle = () => setSettle({ open: false, session: null, pay: EMPTY_PAY, busy: false });

  const settleTotal = tabOutstanding(settle.session);
  const settleEval = evaluatePayment(settle.pay, settleTotal);
  const settleChange = settleEval.change || 0;
  const canSettle = !settle.busy && Boolean(settle.session) && settleEval.valid;

  /** Step 1: "Are you sure this is cash/card/split?" */
  const askSettleTab = () => {
    if (!canSettle) { toast.error(settleEval.reason || "Enter the payment amount first."); return; }
    setConfirmPay({ open: true, kind: "tab", method: settle.pay.method, total: settleTotal, ev: settleEval, label: tabLabel(settle.session) });
  };

  /** Step 2: record payment then close the tab — same two calls the Staff dashboard makes. */
  const settleTab = async () => {
    const sess = settle.session;
    if (!sess || !canSettle) return;
    setSettle((s) => ({ ...s, busy: true }));
    const cash = settleEval.cash; const card = settleEval.card; const eft = settleEval.eft || 0;
    const tenders = (settleEval.payments || []).map((t) => ({ ...t, id: `t-${Date.now().toString(36)}-${Math.random().toString(36).slice(2, 8)}` }));
    try {
      let offline = false;
      if (isLocalId(sess.id)) {
        await settleOffline(sess, tenders, settleTotal);
        offline = true;
      } else {
        try {
          if (settleTotal > 0 && tenders.length) {
            // Session 31.26 — append every tender (cash / cards / EFT) to
            // the tab's ledger; the same client ids make the offline
            // fallback below idempotent with this call.
            await http.post(`/staff/table-sessions/${sess.id}/payments`, { payments: tenders });
          }
          await http.patch(`/staff/table-sessions/${sess.id}/close`);
        } catch (e) {
          if (!isNetErr(e)) throw e;
          await settleOffline(sess, tenders, settleTotal);
          offline = true;
        }
      }
      const receipt = {
        session_id: sess.id,
        table_number: tabLabel(sess),
        total: settleTotal,
        cash_received: cash,
        card_received: card,
        eft_received: eft,
        payments: tenders,
        tip_amount: settleEval.tip || 0,
        payment_method: settle.pay.method,
        change_due: settleChange > 0 ? settleChange : 0,
        created_at: new Date().toISOString(),
        sold_by: userLabel,
        orders: sess.orders || [],
        order: { items: tabItems(sess) },
      };
      closeSettle();
      setConfirmPay({ open: false });
      if (activeTab?.id === sess.id) setActiveTab(null);
      if (!offline) await fetchTabs(true);
      toast.success(`${tabLabel(sess)} settled · ${formatPrice(settleTotal)}`, {
        description: settleChange > 0 ? `Change due ${formatPrice(settleChange)}` : (offline ? "Offline — saved on this device; syncs when internet returns." : undefined),
        action: { label: "Print slip", onClick: () => printSale(receipt) },
      });
    } catch (e) {
      if (e?.response?.status === 401) { onLogout?.(); return; }
      toast.error(errDetail(e, "Could not settle the tab."));
      setSettle((s) => ({ ...s, busy: false }));
    }
  };

  const openedAgo = (s) => {
    const t = s?.opened_at || s?.created_at;
    if (!t) return "";
    const mins = Math.max(0, Math.round((Date.now() - new Date(t).getTime()) / 60000));
    if (mins < 1) return "just now";
    if (mins < 60) return `${mins} min`;
    const h = Math.floor(mins / 60);
    return `${h}h ${mins % 60}m`;
  };

  // ── Slip printing — Session 30.7: unified printSlip() picks the
  //    silent native path (Bluetooth ESC/POS on Android, raw spooler on
  //    Windows) and falls back to the browser dialog elsewhere.
  const printSale = async (sale) => {
    if (!sale) return;
    const items = receiptItems(sale);
    const lines = items.map((it) => {
      const mods = [];
      (it.selected_sides || []).forEach((s) => mods.push(s.charged && Number(s.price) > 0 ? `${s.name} (+${formatPrice(s.price)})` : s.name));
      (it.prep_selections || []).forEach((p) => mods.push(`${p.label}: ${p.choice}`));
      if (it.note) mods.push(`"${it.note}"`);
      return {
        qty: it.quantity || 1,
        name: it.name || "",
        price: Number(it.line_total ?? (Number(it.price || 0) * Number(it.quantity || 0))),
        modifiers: mods,
      };
    });
    const total = Number(sale.total || 0);
    const slipFee = Number(sale.service_fee_amount || 0);
    const itemsTotal = Math.round((total - slipFee) * 100) / 100;
    const vat = vatRate > 0 ? Math.round((itemsTotal * vatRate / (100 + vatRate)) * 100) / 100 : 0;
    const payments = [];
    if (Array.isArray(sale.payments) && sale.payments.length) {
      let cardN = 0;
      sale.payments.forEach((p) => {
        if (p.method === "card") cardN += 1;
        const lbl = p.label || (p.method === "card" ? `Card ${cardN}` : p.method === "eft" ? "EFT" : "Cash");
        payments.push({ label: `${lbl}${Number(p.tip) > 0 ? ` (tip ${formatPrice(p.tip)})` : ""}`, amount: Number(p.amount) || 0 });
      });
    } else {
      if (Number(sale.cash_received || 0) > 0) payments.push({ label: "Cash", amount: Number(sale.cash_received) });
      if (Number(sale.card_received || 0) > 0) payments.push({ label: "Card", amount: Number(sale.card_received) });
      if (Number(sale.eft_received || 0) > 0) payments.push({ label: "EFT", amount: Number(sale.eft_received) });
    }
    const orderNos = [
      ...new Set([sale.order?.order_number, ...((sale.orders || []).map((o) => o.order_number))].filter(Boolean)),
    ];
    const r = await printSlip({
      business: { name: business?.name || catalog?.business?.name || "Receipt", phone: business?.support_phone || "" },
      tableLabel: sale.table_number || "",
      orderLabel: orderNos.length ? `Order ${orderNos.join(", ")}` : (sale.sale_no ? `Sale #${sale.sale_no}` : ""),
      header: sale.payment_method === "unpaid" ? "RUNNING BILL — not yet paid" : "",
      dateStr: sale.created_at ? new Date(sale.created_at).toLocaleString() : new Date().toLocaleString(),
      servedBy: sale.sold_by || "",
      lines,
      subtotal: Math.round((itemsTotal - vat) * 100) / 100,
      vatRate,
      vatAmount: vat,
      serviceFeePct: slipFee > 0 ? Number(sale.service_fee_pct || 0) : 0,
      serviceFeeAmount: slipFee,
      total,
      payments,
      changeDue: Number(sale.change_due || 0),
      footer: "Thank you!",
      formatPrice,
    });
    if (!r.ok) {
      toast.error("Could not print the slip", {
        description: r.error || "Check Printer setup.",
        action: { label: "Printer setup", onClick: () => setPrinterOpen(true) },
      });
    } else if (r.method === "bluetooth" || r.method === "windows-raw") {
      toast.success("Slip sent to the printer");
    }
  };

  // Normalise a "recent sales" row into the same shape POST returns so
  // the receipt renderer + void dialog can use either.
  const saleFromRecent = (row) => ({
    session_id: row.session_id,
    sale_no: row.sale_no,
    table_number: row.table_number,
    total: row.total,
    cash_received: row.cash_received,
    card_received: row.card_received,
    payment_method: row.payment_method,
    change_due: 0,
    created_at: row.created_at,
    sold_by: row.sold_by,
    orders: row.orders || [],
    order: { items: (row.orders || []).flatMap((o) => o.items || []) },
  });

  const receiptItems = (sale) => {
    const items = sale?.order?.items || [];
    return items.filter((it) => !it.voided);
  };

  // ── Void flow ────────────────────────────────────────────────────
  const submitVoid = async () => {
    const { sale, pin } = voidDialog;
    if (!sale || !pin.trim()) { toast.error("Enter the manager PIN."); return; }
    setVoidDialog((v) => ({ ...v, busy: true }));
    try {
      await http.post(`/retail/sales/${sale.session_id}/void`, { manager_pin: pin.trim() });
      toast.success(`${sale.table_number} voided`);
      setVoidDialog({ open: false, sale: null, pin: "", busy: false });
      fetchRecentSales(true);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Void failed");
      setVoidDialog((v) => ({ ...v, busy: false }));
    }
  };

  // ── Prep strip: sales still being prepared / ready for delivery ──
  const prepSales = useMemo(
    () => recentSales.filter((s) => s.status === "preparing" || s.status === "ready").slice(0, 8),
    [recentSales],
  );

  const statusTone = {
    preparing: "border-amber-400/50 bg-amber-500/10 text-amber-700 dark:text-amber-300",
    ready: "border-emerald-400/50 bg-emerald-500/10 text-emerald-700 dark:text-emerald-300",
    completed: "border-border bg-secondary/50 text-muted-foreground",
    voided: "border-destructive/40 bg-destructive/10 text-destructive",
  };

  // ─────────────────────────────────────────────────────────────────
  return (
    <div className="flex h-screen flex-col overflow-hidden bg-background" data-testid="retail-dashboard">
      {/* ── Header ── */}
      <header className="flex shrink-0 flex-wrap items-center justify-between gap-2 border-b border-border bg-card px-3 py-2 sm:gap-3 sm:px-6 sm:py-2.5">
        <div className="flex min-w-0 items-center gap-3">
          <div className="flex h-9 w-9 shrink-0 items-center justify-center rounded-xl bg-[hsl(var(--primary))] text-primary-foreground">
            <ShoppingBag className="h-5 w-5" />
          </div>
          <div className="min-w-0">
            <h1 className="truncate text-base font-semibold leading-tight">{business?.name || "Retail"}</h1>
            <p className="text-xs text-muted-foreground">Retail POS · {userLabel}</p>
          </div>
        </div>
        <div className="flex flex-wrap items-center justify-end gap-1.5 sm:gap-2">
          <Badge
            variant="outline"
            className={`hidden gap-1.5 rounded-full px-2.5 sm:inline-flex ${connected ? "border-emerald-400/50 bg-emerald-500/10 text-emerald-700 dark:text-emerald-300" : "border-amber-400/50 bg-amber-500/10 text-amber-700 dark:text-amber-300"}`}
            data-testid="retail-live-badge"
          >
            {connected ? <Wifi className="h-3 w-3" /> : <WifiOff className="h-3 w-3" />}
            {connected ? "Live" : "Reconnecting"}
          </Badge>
          <MeshStatusPill />
          {pendingApprovals.length > 0 ? (
            // Session 31.8 — "needs approval" badge; pulses, scrolls to the card.
            <Button
              variant="outline"
              className="relative rounded-xl border-sky-400/60 bg-sky-50 text-sky-900 hover:bg-sky-100 dark:border-sky-700 dark:bg-sky-950/40 dark:text-sky-100"
              onClick={() => approvalsCardRef.current?.scrollIntoView({ behavior: "smooth", block: "start" })}
              data-testid="retail-approvals-badge"
              aria-label={`${pendingApprovals.length} orders need approval`}
            >
              <ShieldAlert className="h-4 w-4" />
              <span className="hidden sm:inline">Approve</span>
              <span className="ml-0.5 inline-flex h-5 min-w-5 items-center justify-center rounded-full bg-sky-500 px-1.5 text-[11px] font-bold text-white" data-testid="retail-approvals-badge-count">
                {pendingApprovals.length}
              </span>
              <span className="absolute -right-1 -top-1 h-2.5 w-2.5 animate-ping rounded-full bg-sky-500" aria-hidden="true" />
            </Button>
          ) : null}
          <Button
            variant="outline"
            className="relative rounded-xl border-amber-500/60 bg-amber-500/10 text-amber-900 hover:bg-amber-500/20 hover:text-amber-950 dark:text-amber-200 dark:hover:text-amber-100"
            onClick={() => { setTabsOpen(true); fetchTabs(false); }}
            title="Open tabs & tables"
            data-testid="retail-tabs-button"
          >
            <ClipboardList className="h-4 w-4" />
            <span className="hidden sm:inline">Tabs</span>
            {tabs.length > 0 ? (
              <span className="ml-0.5 inline-flex h-5 min-w-5 items-center justify-center rounded-full bg-[hsl(var(--primary))] px-1.5 text-[11px] font-bold text-primary-foreground" data-testid="retail-tabs-count">
                {tabs.length}
              </span>
            ) : null}
            {cashDueTabs.length > 0 ? (
              <span className="ml-1 inline-flex h-5 items-center gap-1 rounded-full border border-amber-500/40 bg-amber-500/15 px-1.5 text-[10px] font-semibold text-amber-800 dark:text-amber-200" title={`${cashDueTabs.length} online order${cashDueTabs.length === 1 ? "" : "s"} waiting for cash`} data-testid="retail-tabs-cash-due-count">
                <Banknote className="h-3 w-3" /> {cashDueTabs.length} cash due
              </span>
            ) : null}
          </Button>
          <Button
            variant="outline"
            className="rounded-xl border-teal-500/60 bg-teal-500/10 text-teal-800 hover:bg-teal-500/20 hover:text-teal-900 dark:text-teal-200 dark:hover:text-teal-100"
            onClick={() => { setRecentOpen(true); fetchRecentSales(false); }}
            title="Recent sales & receipts"
            data-testid="retail-recent-sales-button"
          >
            <History className="h-4 w-4" />
            <span className="hidden sm:inline">Sales & receipts</span>
          </Button>
          {/* Session 30.13 — full-screen POS mode (exit with the same button) */}
          <FullscreenButton testId="retail-fullscreen-button" />
          <Button
            variant="outline"
            size="icon"
            className="hidden h-10 w-10 rounded-xl sm:inline-flex"
            onClick={() => setPrinterOpen(true)}
            title="Printer setup"
            aria-label="Printer setup"
            data-testid="retail-printer-settings-button"
          >
            <Printer className="h-4 w-4" />
          </Button>
          {/* Session 30.4 — theme + logout styled exactly like the Kitchen /
              Bar station headers (outlined icon box + outlined red logout). */}
          {onToggleTheme ? (
            <Button
              variant="outline"
              size="icon"
              className="h-10 w-10 rounded-xl"
              onClick={onToggleTheme}
              title={theme === "dark" ? "Switch to light mode" : "Switch to dark mode"}
              aria-label={theme === "dark" ? "Switch to light mode" : "Switch to dark mode"}
              data-testid="retail-theme-toggle"
            >
              {theme === "dark" ? <Sun className="h-4 w-4" /> : <Moon className="h-4 w-4" />}
            </Button>
          ) : null}
          <Button
            variant="outline"
            className="rounded-xl border-destructive/40 text-destructive hover:border-destructive hover:bg-destructive/10 hover:text-destructive"
            onClick={onLogout}
            data-testid="retail-logout-button"
          >
            <LogOut className="h-4 w-4" />
            <span className="hidden sm:inline">Logout</span>
          </Button>
        </div>
      </header>

      {/* ── Prep strip (kitchen/bar sales in flight) ── */}
      {prepSales.length > 0 ? (
        <div className="flex shrink-0 items-center gap-2 overflow-x-auto border-b border-border bg-card/60 px-4 py-2 sm:px-6" data-testid="retail-prep-strip">
          {prepSales.map((s) => (
            <span
              key={s.session_id}
              className={`inline-flex shrink-0 items-center gap-1.5 rounded-full border px-3 py-1 text-xs font-medium ${statusTone[s.status] || statusTone.completed}`}
              data-testid={`retail-prep-chip-${s.sale_no}`}
            >
              {s.status === "ready" ? <span className="h-1.5 w-1.5 animate-pulse rounded-full bg-emerald-500" /> : <ChefHat className="h-3 w-3" />}
              {s.table_number} · {s.status === "ready" ? "Ready — runner collecting" : "Preparing"}
            </span>
          ))}
        </div>
      ) : null}

      {pendingApprovals.length ? (
        <div ref={approvalsCardRef} className="shrink-0 border-b border-border px-4 py-3 sm:px-6">
          <PendingApprovalsCard
            orders={pendingApprovals}
            busyId={approvalBusyId}
            onAccept={acceptPendingApproval}
            onDecline={declinePendingApproval}
            testIdPrefix="retail-approvals"
          />
        </div>
      ) : null}

      {/* ── Main split layout ── */}
      <div className="relative flex min-h-0 flex-1">
        {/* ══ LEFT: catalog ══ */}
        <div className="flex min-w-0 flex-1 flex-col">
          {/* Search + categories */}
          <div className="shrink-0 space-y-2.5 border-b border-border px-4 py-3 sm:px-6">
            <div className="relative">
              <Search className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
              <Input
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                placeholder="Search products…"
                className="h-11 rounded-xl pl-9 pr-9"
                data-testid="retail-search-input"
              />
              {search ? (
                <button
                  className="absolute right-2.5 top-1/2 -translate-y-1/2 rounded-full p-1 text-muted-foreground hover:bg-secondary hover:text-foreground"
                  onClick={() => setSearch("")}
                  data-testid="retail-search-clear"
                  aria-label="Clear search"
                >
                  <X className="h-4 w-4" />
                </button>
              ) : null}
            </div>
            <div className="flex items-center gap-2 overflow-x-auto pb-0.5" data-testid="retail-category-rail">
              <button
                className={`shrink-0 rounded-full border px-4 py-2 text-sm font-medium transition-colors ${activeCat === "all" && !search ? "border-transparent bg-[hsl(var(--primary))] text-primary-foreground" : "border-border bg-card text-foreground hover:bg-secondary"}`}
                onClick={() => { setActiveCat("all"); setSearch(""); }}
                data-testid="retail-category-pill-all"
              >
                All
              </button>
              {categories.map((c) => (
                <button
                  key={c.id}
                  className={`shrink-0 rounded-full border px-4 py-2 text-sm font-medium transition-colors ${activeCat === c.id && !search ? "border-transparent bg-[hsl(var(--primary))] text-primary-foreground" : "border-border bg-card text-foreground hover:bg-secondary"}`}
                  onClick={() => { setActiveCat(c.id); setSearch(""); }}
                  data-testid={`retail-category-pill-${c.id}`}
                >
                  {c.name}
                </button>
              ))}
            </div>
          </div>

          {/* Session 30.17 — Quick Keys: one-tap best sellers */}
          {!search && quickKeys.length > 0 ? (
            <div className={`shrink-0 border-b border-border/70 px-4 sm:px-6 ${quickKeysOn ? "pb-2.5 pt-1" : "py-1"}`} data-testid="retail-quick-keys">
              <div className="mb-1.5 flex items-center justify-between gap-2 text-[11px] font-semibold uppercase tracking-wide text-muted-foreground">
                <span className="flex items-center gap-1.5">
                  <Zap className="h-3.5 w-3.5 text-[hsl(var(--accent))]" />
                  Quick keys · best sellers
                </span>
                <label className="flex items-center gap-2 normal-case tracking-normal" data-testid="retail-quick-keys-toggle-row">
                  <span className="text-[11px] font-medium">{quickKeysOn ? "On" : "Off"}</span>
                  <Switch checked={quickKeysOn} onCheckedChange={toggleQuickKeys} aria-label="Show quick keys" data-testid="retail-quick-keys-toggle" />
                </label>
              </div>
              {quickKeysOn ? (
              <div className="flex gap-2 overflow-x-auto pb-1 [scrollbar-width:thin]">
                {quickKeys.map((item) => {
                  const q = qtyByItem[item.id] || 0;
                  return (
                    <button
                      key={item.id}
                      type="button"
                      onClick={() => addToCart(item)}
                      className="relative flex h-14 min-w-[11.5rem] max-w-[14rem] shrink-0 items-center gap-2.5 rounded-xl border border-border bg-card pl-1.5 pr-3 text-left transition-transform hover:border-[hsl(var(--primary))]/40 hover:shadow-sm focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[hsl(var(--primary))] active:scale-[0.98]"
                      data-testid={`retail-quick-key-${item.id}`}
                    >
                      <ProductImage src={item.image} alt={item.name} className="h-11 w-11 shrink-0 rounded-lg" />
                      <span className="min-w-0 flex-1">
                        <span className="block truncate text-sm font-semibold leading-tight">{item.name}</span>
                        <span className="block text-xs tabular-nums text-muted-foreground">{formatPrice(item.price)}{needsOptions(item) ? " · options" : ""}</span>
                      </span>
                      {q > 0 ? (
                        <span className="absolute -right-1 -top-1 flex h-5 min-w-5 items-center justify-center rounded-full bg-[hsl(var(--accent))] px-1 text-[10px] font-bold text-[hsl(var(--accent-foreground))] shadow" data-testid={`retail-quick-key-qty-${item.id}`}>{q}</span>
                      ) : null}
                    </button>
                  );
                })}
              </div>
              ) : null}
            </div>
          ) : null}

          {/* Product grid — native scrolling (Radix ScrollArea's display:table
              viewport stopped `truncate` from shrinking and clipped the right
              edge of cards / cart rows). */}
          <div className="min-h-0 flex-1 overflow-y-auto overflow-x-hidden">
            <div className="grid grid-cols-2 gap-2.5 p-4 sm:px-6 md:grid-cols-3 xl:grid-cols-4 2xl:grid-cols-5" data-testid="retail-product-grid">
              {loadingCatalog
                ? Array.from({ length: 8 }).map((_, i) => (
                    <Skeleton key={i} className="h-44 rounded-2xl" />
                  ))
                : visibleItems.length === 0
                ? (
                  <div className="col-span-full flex flex-col items-center gap-2 py-16 text-center" data-testid="retail-empty-products">
                    <Search className="h-8 w-8 text-muted-foreground/50" />
                    <p className="text-sm font-medium">No products found</p>
                    <p className="text-xs text-muted-foreground">{search ? "Try a different search." : "Add menu items on the Owner dashboard."}</p>
                  </div>
                )
                : visibleItems.map((item) => {
                    const configurable = needsOptions(item);
                    const inCartQty = qtyByItem[item.id] || 0;
                    return (
                      <button
                        key={item.id}
                        onClick={() => addToCart(item)}
                        aria-disabled={item.sellable === false}
                        className="group relative flex min-w-0 flex-col overflow-hidden rounded-2xl border border-border bg-card text-left transition-transform hover:border-[hsl(var(--primary))]/40 hover:shadow-sm focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[hsl(var(--primary))] active:scale-[0.98]"
                        data-testid={`retail-product-card-${item.id}`}
                      >
                        <ProductImage src={item.image} alt={item.name} className="aspect-[4/3] w-full" />
                        {inCartQty > 0 ? (
                          <span className="absolute right-2 top-2 flex h-6 min-w-6 items-center justify-center rounded-full bg-[hsl(var(--accent))] px-1.5 text-xs font-bold text-[hsl(var(--accent-foreground))] shadow" data-testid={`retail-product-qty-${item.id}`}>
                            {inCartQty}
                          </span>
                        ) : null}
                        {item.out_of_stock ? (
                          <span className={`absolute inset-x-2 bottom-[4.6rem] inline-flex items-center justify-center rounded-full px-2 py-0.5 text-[10px] font-semibold uppercase tracking-wide shadow ${item.sellable === false ? "bg-destructive text-destructive-foreground" : "bg-amber-500/90 text-black"}`} data-testid={`retail-oos-${item.id}`}>
                            {item.sellable === false ? "Out of stock" : "Out of stock · selling"}
                          </span>
                        ) : null}
                        {configurable ? (
                          <span className="absolute left-2 top-2 inline-flex items-center gap-1 rounded-full bg-card/90 px-2 py-0.5 text-[10px] font-medium text-foreground shadow backdrop-blur" data-testid={`retail-product-options-badge-${item.id}`}>
                            <SlidersHorizontal className="h-3 w-3" /> Options
                          </span>
                        ) : null}
                        <div className="flex min-w-0 flex-1 flex-col justify-between gap-1.5 p-3">
                          <div className="min-w-0">
                            <p className="line-clamp-2 text-sm font-semibold leading-snug">{item.name}</p>
                            <p className="mt-0.5 truncate text-[11px] text-muted-foreground">
                              {item._cat}{item.department ? ` · ${item.department}` : ""}
                            </p>
                          </div>
                          <p className="text-sm font-bold tabular-nums">{formatPrice(item.price)}</p>
                        </div>
                      </button>
                    );
                  })}
            </div>
          </div>
        </div>

        {/* ══ RIGHT: ticket panel ══ */}
        <aside
          className={`${mobileTicketOpen ? "fixed inset-0 z-[60] flex w-full" : "hidden"} flex-col border-border bg-card md:static md:z-auto md:flex md:w-[340px] md:shrink-0 md:border-l lg:w-[400px]`}
          data-testid="retail-ticket-panel"
        >
          {/* Phone-only header for the ticket sheet */}
          <div className="flex shrink-0 items-center justify-between border-b border-border px-4 py-2.5 md:hidden" data-testid="retail-ticket-mobile-header">
            <p className="text-sm font-semibold">{activeTab ? `Adding to ${tabLabel(activeTab)}` : "Current sale"}</p>
            <Button variant="outline" size="icon" className="h-9 w-9 rounded-xl" onClick={() => setMobileTicketOpen(false)} aria-label="Back to products" data-testid="retail-ticket-mobile-close">
              <X className="h-4 w-4" />
            </Button>
          </div>
          {stage === "done" && lastSale ? (
            /* ── SALE COMPLETE ── */
            <div className="flex min-h-0 flex-1 flex-col items-center justify-center gap-4 p-6 text-center" data-testid="retail-sale-complete">
              <div className="flex h-14 w-14 items-center justify-center rounded-full bg-emerald-500/15 text-emerald-600 dark:text-emerald-400">
                <Banknote className="h-7 w-7" />
              </div>
              <div>
                <h2 className="text-xl font-bold">{lastSale.table_number} complete</h2>
                <p className="mt-1 text-sm text-muted-foreground">
                  {formatPrice(lastSale.total)} · {payMethodLabel(lastSale.payment_method)}
                </p>
              </div>
              {lastSale.change_due > 0 ? (
                <div className="w-full rounded-2xl border border-emerald-400/50 bg-emerald-500/10 px-4 py-4" data-testid="retail-change-due">
                  <p className="text-xs font-medium uppercase tracking-wide text-emerald-700 dark:text-emerald-300">Change due</p>
                  <p className="text-3xl font-bold tabular-nums text-emerald-700 dark:text-emerald-300">{formatPrice(lastSale.change_due)}</p>
                </div>
              ) : null}
              {lastSale.kitchen_bar_items > 0 ? (
                <p className="flex items-center gap-1.5 text-xs text-muted-foreground">
                  <ChefHat className="h-3.5 w-3.5" /> {lastSale.kitchen_bar_items} item{lastSale.kitchen_bar_items === 1 ? "" : "s"} sent to kitchen/bar — watch the prep strip
                </p>
              ) : null}
              <div className="flex w-full flex-col gap-2">
                <Button
                  variant="outline"
                  className="h-12 w-full rounded-xl"
                  onClick={() => printSale(lastSale)}
                  data-testid="retail-print-slip-button"
                >
                  <Printer className="h-4 w-4" />
                  Print slip
                </Button>
                <Button
                  className="h-12 w-full rounded-xl bg-[hsl(var(--accent))] text-[hsl(var(--accent-foreground))] hover:bg-[hsl(var(--accent))]/90"
                  onClick={() => { setStage("cart"); setLastSale(null); setMobileTicketOpen(false); }}
                  data-testid="retail-new-sale-button"
                >
                  <Plus className="h-4 w-4" />
                  New sale
                </Button>
              </div>
            </div>
          ) : (
            <>
              {/* ── Ticket lines ── */}
              <div className="flex shrink-0 items-center justify-between px-4 pb-1 pt-3">
                <h2 className="text-sm font-semibold uppercase tracking-wide text-muted-foreground">{activeTab ? "Adding to tab" : "Current sale"}</h2>
                {cart.length > 0 ? (
                  <button className="text-xs font-medium text-destructive hover:underline" onClick={clearCart} data-testid="retail-clear-cart-button">
                    Clear
                  </button>
                ) : null}
              </div>
              {activeTab ? (
                <div className="mx-4 mb-2 flex shrink-0 items-center gap-2 rounded-xl border border-[hsl(var(--primary))]/30 bg-[hsl(var(--primary))]/10 px-3 py-2" data-testid="retail-active-tab-banner">
                  <ClipboardList className="h-4 w-4 shrink-0 text-[hsl(var(--primary))]" />
                  <div className="min-w-0 flex-1">
                    <p className="truncate text-sm font-semibold" data-testid="retail-active-tab-name">{tabLabel(activeTab)}</p>
                    <p className="text-[11px] text-muted-foreground">
                      {tabItemCount(activeTab)} item{tabItemCount(activeTab) === 1 ? "" : "s"} · {formatPrice(tabTotal(activeTab))} so far
                    </p>
                  </div>
                  <button className="rounded-md p-1 text-muted-foreground hover:text-foreground" onClick={() => setActiveTab(null)} aria-label="Stop adding to this tab" title="Back to walk-in sale" data-testid="retail-active-tab-clear">
                    <X className="h-4 w-4" />
                  </button>
                </div>
              ) : null}
              <div className="min-h-0 flex-1 overflow-y-auto overflow-x-hidden px-4">
                {cart.length === 0 ? (
                  <div className="flex flex-col items-center gap-2 py-16 text-center" data-testid="retail-empty-cart">
                    <ShoppingBag className="h-8 w-8 text-muted-foreground/40" />
                    <p className="text-sm font-medium">No items yet</p>
                    <p className="text-xs text-muted-foreground">Tap products to add them to the sale.</p>
                  </div>
                ) : (
                  <div className="space-y-2 pb-3">
                    {cart.map((l) => {
                      const summary = lineOptionsSummary(l);
                      return (
                        <div key={l.key} className="flex w-full min-w-0 items-center gap-2 rounded-xl bg-secondary/50 px-3 py-2" data-testid={`retail-cart-line-${l.id}`}>
                          <div className="min-w-0 flex-1">
                            <p className="truncate text-sm font-medium">{l.name}</p>
                            {summary ? (
                              <p className="line-clamp-2 text-[11px] leading-snug text-muted-foreground" data-testid={`retail-cart-line-options-${l.id}`}>{summary}</p>
                            ) : null}
                            <p className="text-xs tabular-nums text-muted-foreground">{formatPrice(lineUnitPrice(l))} each</p>
                          </div>
                          <div className="flex shrink-0 items-center gap-1">
                            <Button variant="outline" size="icon" className="h-8 w-8 rounded-lg" onClick={() => changeQty(l.key, -1)} data-testid={`retail-qty-minus-${l.id}`}>
                              <Minus className="h-3.5 w-3.5" />
                            </Button>
                            <span className="w-7 text-center text-sm font-bold tabular-nums" data-testid={`retail-qty-${l.id}`}>{l.qty}</span>
                            <Button variant="outline" size="icon" className="h-8 w-8 rounded-lg" onClick={() => changeQty(l.key, 1)} data-testid={`retail-qty-plus-${l.id}`}>
                              <Plus className="h-3.5 w-3.5" />
                            </Button>
                          </div>
                          <div className="w-[4.25rem] shrink-0 text-right text-sm font-semibold tabular-nums">{formatPrice(lineUnitPrice(l) * l.qty)}</div>
                          <button className="shrink-0 rounded-md p-1 text-muted-foreground hover:text-destructive" onClick={() => removeLine(l.key)} data-testid={`retail-remove-line-${l.id}`} aria-label={`Remove ${l.name}`}>
                            <Trash2 className="h-3.5 w-3.5" />
                          </button>
                        </div>
                      );
                    })}
                  </div>
                )}
              </div>

              {/* ── Totals + payment ── */}
              <div className="shrink-0 space-y-3 border-t border-border p-4">
                <div className="space-y-1">
                  <div className="flex justify-between text-xs text-muted-foreground">
                    <span>Items</span>
                    <span className="tabular-nums" data-testid="retail-cart-count">{cartCount}</span>
                  </div>
                  {vatRate > 0 ? (
                    <div className="flex justify-between text-xs text-muted-foreground">
                      <span>VAT {vatRate}% (incl.)</span>
                      <span className="tabular-nums">{formatPrice(vatPortion)}</span>
                    </div>
                  ) : null}
                  {serviceFee > 0 ? (
                    <div className="flex justify-between text-xs text-muted-foreground" data-testid="retail-cart-service-fee">
                      <span>Service fee {serviceFeePct}%</span>
                      <span className="tabular-nums">{formatPrice(serviceFee)}</span>
                    </div>
                  ) : null}
                  <div className="flex items-baseline justify-between">
                    <span className="text-sm font-semibold">Total{serviceFee > 0 ? " (incl. fee)" : ""}</span>
                    <span className="text-2xl font-bold tabular-nums" data-testid="retail-cart-total">{formatPrice(chargeTotal)}</span>
                  </div>
                </div>

                {stage === "pay" ? (
                  <div className="space-y-3" data-testid="retail-payment-panel">
                    <PaymentFields total={chargeTotal} pay={salePay} onChange={setSalePay} testPrefix="retail" />

                    <div className="grid grid-cols-[auto,1fr] gap-2">
                      <Button variant="outline" className="h-12 rounded-xl" onClick={() => setStage("cart")} data-testid="retail-back-to-cart-button">
                        Back
                      </Button>
                      <Button
                        className="h-12 rounded-xl bg-[hsl(var(--accent))] text-base font-semibold text-[hsl(var(--accent-foreground))] hover:bg-[hsl(var(--accent))]/90"
                        disabled={!canComplete}
                        onClick={askCompleteSale}
                        data-testid="retail-complete-sale-button"
                      >
                        {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : null}
                        Complete sale · {formatPrice(chargeTotal)}
                      </Button>
                    </div>
                  </div>
                ) : activeTab ? (
                  <div className="grid grid-cols-[1fr,auto] gap-2">
                    <Button
                      className="h-12 rounded-xl bg-[hsl(var(--primary))] text-base font-semibold text-primary-foreground hover:bg-[hsl(var(--primary))]/90"
                      disabled={cart.length === 0 || busy}
                      onClick={addCartToTab}
                      data-testid="retail-add-to-tab-button"
                    >
                      {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : <ClipboardList className="h-4 w-4" />}
                      Add to tab · {formatPrice(cartTotal)}
                    </Button>
                    {/* Session 30.16 — settling always lives bottom-right */}
                    <Button
                      className="h-12 rounded-xl bg-[hsl(var(--accent))] px-4 font-semibold text-[hsl(var(--accent-foreground))] hover:bg-[hsl(var(--accent))]/90"
                      disabled={busy || cart.length > 0}
                      title={cart.length > 0 ? "Add or clear the items in the sale first" : `Settle ${tabLabel(activeTab)}`}
                      onClick={() => openSettle(activeTab)}
                      data-testid="retail-settle-tab-button"
                    >
                      <Banknote className="h-4 w-4" />
                      Settle tab
                    </Button>
                  </div>
                ) : (
                  <div className="grid grid-cols-[auto,1fr] gap-2">
                    <Button
                      variant="outline"
                      className="h-12 rounded-xl px-3"
                      onClick={() => { setTabsOpen(true); fetchTabs(true); }}
                      title="Open a tab or add this sale to an existing one"
                      data-testid="retail-open-tab-button"
                    >
                      <ClipboardList className="h-4 w-4" />
                      <span className="hidden sm:inline">Tab</span>
                    </Button>
                    <Button
                      className="h-12 rounded-xl bg-[hsl(var(--accent))] text-base font-semibold text-[hsl(var(--accent-foreground))] hover:bg-[hsl(var(--accent))]/90"
                      disabled={cart.length === 0}
                      onClick={() => setStage("pay")}
                      data-testid="retail-charge-button"
                    >
                      Charge {formatPrice(chargeTotal)}
                    </Button>
                  </div>
                )}
              </div>
            </>
          )}
        </aside>
      </div>

      {/* ── Phone-only pinned sale bar (tablet/desktop show the side panel) ── */}
      {!mobileTicketOpen ? (
        <div className="flex shrink-0 items-center gap-3 border-t border-border bg-card px-3 py-2.5 md:hidden" data-testid="retail-mobile-sale-bar">
          <div className="min-w-0 flex-1">
            <p className="text-[11px] font-semibold uppercase tracking-wide text-muted-foreground">{activeTab ? `Adding to ${tabLabel(activeTab)}` : "Current sale"}</p>
            <p className="truncate text-base font-semibold tabular-nums" data-testid="retail-mobile-sale-summary">
              {cartCount ? `${cartCount} item${cartCount === 1 ? "" : "s"} · ${formatPrice(cartTotal)}` : "Nothing added yet"}
            </p>
          </div>
          <Button
            className="h-12 rounded-xl bg-[hsl(var(--accent))] px-5 text-[hsl(var(--accent-foreground))] hover:bg-[hsl(var(--accent))]/90"
            onClick={() => setMobileTicketOpen(true)}
            data-testid="retail-mobile-view-sale-button"
          >
            <ShoppingBag className="h-4 w-4" />
            {stage === "done" ? "View receipt" : cartCount ? "View sale" : "Open sale"}
          </Button>
        </div>
      ) : null}

      {/* ── Item options (sides / prep / note) ── */}
      <ItemOptionsDialog
        item={optionsItem}
        sidesLibrary={catalog?.sides_library || []}
        onClose={() => setOptionsItem(null)}
        onAdd={(cfg) => { pushLine(cfg); setOptionsItem(null); }}
      />

      {/* ── Tabs dialog: open a tab, add the cart to one, settle & close ── */}
      <Dialog open={tabsOpen} onOpenChange={setTabsOpen}>
        <DialogContent className="flex max-h-[92vh] max-w-3xl flex-col overflow-hidden rounded-2xl p-0" data-testid="retail-tabs-dialog">
          <DialogHeader className="px-6 pb-2 pt-6">
            <DialogTitle className="flex items-center gap-2"><ClipboardList className="h-5 w-5 text-amber-600 dark:text-amber-300" /> Tabs & tables</DialogTitle>
            <DialogDescription>
              Shared live with the Staff and Owner dashboards. <span className="font-medium text-foreground">Add items</span> sends your current sale to a tab, <span className="font-medium text-foreground">Bill</span> prints the running total, <span className="font-medium text-foreground">Settle</span> takes payment and closes it.
            </DialogDescription>
          </DialogHeader>

          <div className="px-6 pb-3">
            <div className="flex gap-2">
              <Input
                value={newTabName}
                onChange={(e) => setNewTabName(e.target.value.slice(0, 20))}
                onKeyDown={(e) => { if (e.key === "Enter") openTab(); }}
                placeholder="New tab name (e.g. Sam, Booth 2)"
                className="h-11 rounded-xl"
                data-testid="retail-new-tab-input"
              />
              <Button
                className="h-11 shrink-0 rounded-xl bg-[hsl(var(--accent))] text-[hsl(var(--accent-foreground))] hover:bg-[hsl(var(--accent))]/90"
                disabled={openingTab || !newTabName.trim()}
                onClick={openTab}
                data-testid="retail-new-tab-submit"
              >
                {openingTab ? <Loader2 className="h-4 w-4 animate-spin" /> : <Plus className="h-4 w-4" />}
                {cart.length ? `Open & add ${cartCount}` : "Open tab"}
              </Button>
            </div>
            {cart.length ? (
              <p className="mt-1.5 text-xs text-muted-foreground" data-testid="retail-tabs-cart-hint">
                Your current sale ({cartCount} item{cartCount === 1 ? "" : "s"} · {formatPrice(cartTotal)}) will be added to the tab you open or pick below.
              </p>
            ) : null}
            {tabs.length > 0 ? (
              <div className="mt-3 flex flex-wrap items-center gap-2" data-testid="retail-tabs-filter">
                <Button
                  type="button"
                  size="sm"
                  variant={tabFilter === "all" ? "default" : "outline"}
                  className="h-8 rounded-full px-3 text-xs"
                  onClick={() => setTabFilter("all")}
                  data-testid="retail-tabs-filter-all"
                >
                  All tabs ({tabs.length})
                </Button>
                <Button
                  type="button"
                  size="sm"
                  variant={tabFilter === "online" ? "default" : "outline"}
                  className="h-8 rounded-full px-3 text-xs"
                  onClick={() => setTabFilter("online")}
                  data-testid="retail-tabs-filter-online"
                >
                  <QrCode className="mr-1 h-3.5 w-3.5" /> Online orders ({onlineTabs.length})
                </Button>
                {cashDueTabs.length > 0 ? (
                  <span className="inline-flex items-center gap-1 rounded-full border border-amber-500/40 bg-amber-500/10 px-2.5 py-1 text-xs font-medium text-amber-800 dark:text-amber-200" data-testid="retail-tabs-cash-due-total">
                    <Banknote className="h-3.5 w-3.5" /> {formatPrice(cashDueTotal)} cash still to collect
                  </span>
                ) : null}
              </div>
            ) : null}
            {tabs.length > 0 ? (
              <div className="relative mt-3">
                <Search className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
                <Input
                  value={tabSearch}
                  onChange={(e) => setTabSearch(e.target.value)}
                  placeholder={`Search ${tabs.length} open tab${tabs.length === 1 ? "" : "s"} & tables…`}
                  className="h-10 rounded-xl pl-9 pr-9"
                  data-testid="retail-tab-search-input"
                />
                {tabSearch ? (
                  <button
                    type="button"
                    className="absolute right-2.5 top-1/2 -translate-y-1/2 rounded-full p-1 text-muted-foreground hover:bg-secondary hover:text-foreground"
                    onClick={() => setTabSearch("")}
                    aria-label="Clear tab search"
                    data-testid="retail-tab-search-clear"
                  >
                    <X className="h-4 w-4" />
                  </button>
                ) : null}
              </div>
            ) : null}
          </div>

          <ScrollArea className="min-h-0 flex-1 px-6 pb-6">
            {loadingTabs && tabs.length === 0 ? (
              <div className="space-y-2 py-2">
                {Array.from({ length: 3 }).map((_, i) => <Skeleton key={i} className="h-20 rounded-xl" />)}
              </div>
            ) : tabs.length === 0 ? (
              <div className="flex flex-col items-center gap-2 py-10 text-center" data-testid="retail-empty-tabs">
                <ClipboardList className="h-8 w-8 text-muted-foreground/40" />
                <p className="text-sm font-medium">No open tabs</p>
                <p className="text-xs text-muted-foreground">Type a name above to open one.</p>
              </div>
            ) : filteredTabs.length === 0 && tabFilter === "online" && !tabSearch.trim() ? (
              <div className="flex flex-col items-center gap-2 py-10 text-center" data-testid="retail-tabs-no-online">
                <QrCode className="h-8 w-8 text-muted-foreground/40" />
                <p className="text-sm font-medium">No online orders right now</p>
                <p className="text-xs text-muted-foreground">Tabs with orders placed from the digital menu appear here, with the cash still owed.</p>
              </div>
            ) : filteredTabs.length === 0 ? (
              <div className="flex flex-col items-center gap-2 py-10 text-center" data-testid="retail-tabs-no-match">
                <Search className="h-8 w-8 text-muted-foreground/40" />
                <p className="text-sm font-medium">No tab matches "{tabSearch.trim()}"</p>
                <p className="text-xs text-muted-foreground">Try a table number, tab name or waiter.</p>
              </div>
            ) : (
              <div className="space-y-2 py-1" data-testid="retail-tabs-list">
                {filteredTabs.map((s) => {
                  const count = tabItemCount(s);
                  const isActive = activeTab?.id === s.id;
                  const preview = tabItems(s).slice(0, 4).map((it) => `${it.quantity}× ${it.name}`).join(", ");
                  return (
                    <div
                      key={s.id}
                      className={`rounded-xl border border-l-4 px-3 py-2.5 ${
                        isActive
                          ? "border-[hsl(var(--primary))]/50 border-l-[hsl(var(--primary))] bg-[hsl(var(--primary))]/5"
                          : Number(s.online_cash_due || 0) > 0
                            ? "border-border border-l-amber-500 bg-amber-500/5"
                            : Number(s.online_orders || 0) > 0
                              ? "border-border border-l-sky-500 bg-sky-500/5"
                              : "border-border border-l-border"
                      }`}
                      data-testid={`retail-tab-row-${s.id}`}
                    >
                      <div className="flex flex-wrap items-center gap-2">
                        <div className="min-w-0 flex-1">
                          <div className="flex flex-wrap items-center gap-x-2 gap-y-1">
                            <p className="min-w-0 max-w-full truncate text-sm font-semibold">{tabLabel(s)}</p>
                            {isActive ? <Badge className="rounded-full bg-[hsl(var(--primary))] text-[10px] text-primary-foreground hover:bg-[hsl(var(--primary))]">Adding</Badge> : null}
                            {s.pending_orders > 0 || s.confirmed_orders > 0 ? (
                              <Badge variant="outline" className={`whitespace-nowrap rounded-full text-[10px] ${statusTone.preparing}`}>In progress</Badge>
                            ) : null}
                            {!(s.pending_orders > 0 || s.confirmed_orders > 0) && Number(s.completed_orders || 0) > 0 && Number(s.online_cash_due || 0) > 0 ? (
                              <Badge variant="outline" className="whitespace-nowrap rounded-full border-emerald-500/40 bg-emerald-500/10 text-[10px] text-emerald-700 dark:text-emerald-300" data-testid={`retail-tab-delivered-${s.id}`}>Delivered · awaiting cash</Badge>
                            ) : null}
                            {Number(s.online_orders || 0) > 0 ? (
                              <Badge variant="outline" className="whitespace-nowrap rounded-full border-sky-500/40 bg-sky-500/10 text-[10px] text-sky-700 dark:text-sky-300" data-testid={`retail-tab-online-${s.id}`}>
                                <QrCode className="mr-1 h-3 w-3" /> Online order
                              </Badge>
                            ) : null}
                            {Number(s.online_cash_due || 0) > 0 ? (
                              <Badge className="whitespace-nowrap rounded-full border border-amber-500/40 bg-amber-500/15 text-[10px] font-semibold text-amber-800 hover:bg-amber-500/15 dark:text-amber-200" data-testid={`retail-tab-cash-due-${s.id}`}>
                                <Banknote className="mr-1 h-3 w-3" /> Cash due {formatPrice(s.online_cash_due)}
                              </Badge>
                            ) : null}
                          </div>
                          <p className="text-xs text-muted-foreground">
                            {count} item{count === 1 ? "" : "s"} · open {openedAgo(s)}{s.assigned_user_name ? ` · ${s.assigned_user_name}` : ""}
                          </p>
                          {s.customer_name || s.customer_phone ? (
                            <p className="mt-0.5 flex flex-wrap items-center gap-x-2 text-xs font-medium text-foreground" data-testid={`retail-tab-guest-${s.id}`}>
                              {s.customer_name ? <span>{s.customer_name}</span> : null}
                              {s.customer_phone ? (
                                <a href={`tel:${String(s.customer_phone).replace(/\s+/g, "")}`} className="inline-flex items-center gap-1 text-sky-700 underline-offset-2 hover:underline dark:text-sky-300" data-testid={`retail-tab-phone-${s.id}`}>
                                  <Phone className="h-3 w-3" /> {s.customer_phone}
                                </a>
                              ) : null}
                              {(s.online_order_numbers || []).length ? <span className="font-mono text-[11px] text-muted-foreground">{s.online_order_numbers.join(", ")}</span> : null}
                            </p>
                          ) : null}
                          {preview ? <p className="mt-0.5 truncate text-[11px] text-muted-foreground/80">{preview}{tabItems(s).length > 4 ? "…" : ""}</p> : null}
                        </div>
                        <p className="text-sm font-bold tabular-nums" data-testid={`retail-tab-total-${s.id}`}>{formatPrice(tabTotal(s))}</p>
                        <div className="flex items-center gap-1.5">
                          <Button
                            variant={isActive ? "secondary" : "outline"}
                            size="sm"
                            className="rounded-lg"
                            onClick={async () => {
                              if (cart.length) {
                                setBusy(true);
                                try {
                                  await http.post(`/staff/table-sessions/${s.id}/items`, { items: cartToOrderItems() });
                                  const n = cartCount;
                                  setCart([]);
                                  toast.success(`${n} item${n === 1 ? "" : "s"} added to ${tabLabel(s)}`);
                                  fetchTabs(true);
                                } catch (e) {
                                  toast.error(errDetail(e, "Could not add items to the tab."));
                                } finally { setBusy(false); }
                              }
                              setActiveTab(s);
                              setTabsOpen(false);
                            }}
                            disabled={busy}
                            data-testid={`retail-tab-add-${s.id}`}
                          >
                            <Plus className="h-3.5 w-3.5" />
                            {cart.length ? `Add ${cartCount}` : "Add items"}
                          </Button>
                          <Button variant="outline" size="sm" className="rounded-lg border-teal-500/60 bg-teal-500/10 text-teal-800 hover:bg-teal-500/20 hover:text-teal-900 dark:text-teal-200" onClick={() => printSale({ session_id: s.id, table_number: tabLabel(s), total: tabTotal(s), payment_method: "unpaid", created_at: s.opened_at || s.created_at, sold_by: s.assigned_user_name, orders: s.orders || [], order: { items: tabItems(s) } })} title="Print running bill" data-testid={`retail-tab-print-${s.id}`}>
                            <Printer className="h-3.5 w-3.5" />
                            <span className="hidden sm:inline">Bill</span>
                          </Button>
                          <Button
                            size="sm"
                            className="rounded-lg bg-[hsl(var(--accent))] text-[hsl(var(--accent-foreground))] hover:bg-[hsl(var(--accent))]/90"
                            onClick={() => { setTabsOpen(false); openSettle(s); }}
                            data-testid={`retail-tab-settle-${s.id}`}
                          >
                            <Banknote className="h-3.5 w-3.5" />
                            Settle
                          </Button>
                        </div>
                      </div>
                    </div>
                  );
                })}
              </div>
            )}
          </ScrollArea>
        </DialogContent>
      </Dialog>

      {/* ── Settle tab dialog (record payment → close) ── */}
      <Dialog open={settle.open} onOpenChange={(open) => { if (!open && !settle.busy) closeSettle(); }}>
        <DialogContent className="max-w-md rounded-2xl" data-testid="retail-settle-dialog">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2"><Banknote className="h-5 w-5" /> Settle {tabLabel(settle.session)}</DialogTitle>
            <DialogDescription>
              {tabItemCount(settle.session)} item{tabItemCount(settle.session) === 1 ? "" : "s"} · records the payment and closes the tab everywhere.
            </DialogDescription>
          </DialogHeader>

          {settle.session ? (
            <div className="space-y-3">
              <div className="max-h-40 space-y-1 overflow-y-auto rounded-xl bg-secondary/50 px-3 py-2 text-sm" data-testid="retail-settle-items">
                {tabItems(settle.session).length === 0 ? (
                  <p className="text-xs text-muted-foreground">No items on this tab yet — closing it will simply remove it.</p>
                ) : tabItems(settle.session).map((it, i) => (
                  <div key={it.id || i} className="flex justify-between gap-2">
                    <span className="min-w-0 truncate">{it.quantity}× {it.name}</span>
                    <span className="shrink-0 tabular-nums">{formatPrice(Number(it.line_total ?? (Number(it.price || 0) * Number(it.quantity || 0))))}</span>
                  </div>
                ))}
              </div>
              <div className="flex items-baseline justify-between">
                <span className="text-sm font-semibold">Total</span>
                <span className="text-2xl font-bold tabular-nums" data-testid="retail-settle-total">{formatPrice(settleTotal)}</span>
              </div>

              {settleTotal > 0 ? (
                <PaymentFields total={settleTotal} pay={settle.pay} onChange={(pay) => setSettle((st) => ({ ...st, pay }))} testPrefix="retail-settle" />
              ) : null}
            </div>
          ) : null}

          <DialogFooter className="gap-2 sm:gap-2">
            <Button variant="outline" className="rounded-xl" onClick={closeSettle} disabled={settle.busy} data-testid="retail-settle-cancel-button">Cancel</Button>
            <Button
              className="rounded-xl bg-[hsl(var(--accent))] font-semibold text-[hsl(var(--accent-foreground))] hover:bg-[hsl(var(--accent))]/90"
              disabled={!canSettle}
              onClick={settleTotal > 0 ? askSettleTab : settleTab}
              data-testid="retail-settle-confirm-button"
            >
              {settle.busy ? <Loader2 className="h-4 w-4 animate-spin" /> : <Check className="h-4 w-4" />}
              {settleTotal > 0 ? `Settle & close · ${formatPrice(settleTotal)}` : "Close tab"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* ── Recent sales dialog ── */}
      <Dialog open={recentOpen} onOpenChange={setRecentOpen}>
        <DialogContent className="max-h-[85vh] max-w-2xl overflow-hidden rounded-2xl p-0">
          <DialogHeader className="px-6 pb-2 pt-6">
            <DialogTitle>Recent sales</DialogTitle>
            <DialogDescription>Reprint slips or void a sale (manager PIN required).</DialogDescription>
          </DialogHeader>
          <ScrollArea className="max-h-[60vh] px-6 pb-6">
            {loadingRecent ? (
              <div className="space-y-2 py-2">
                {Array.from({ length: 4 }).map((_, i) => <Skeleton key={i} className="h-16 rounded-xl" />)}
              </div>
            ) : recentSales.length === 0 ? (
              <div className="flex flex-col items-center gap-2 py-12 text-center" data-testid="retail-empty-sales">
                <History className="h-8 w-8 text-muted-foreground/40" />
                <p className="text-sm font-medium">No sales yet today</p>
              </div>
            ) : (
              <div className="space-y-2 py-1" data-testid="retail-recent-sales-list">
                {recentSales.map((s) => (
                  <div key={s.session_id} className="flex flex-wrap items-center gap-2 rounded-xl border border-border px-3 py-2.5" data-testid={`retail-sale-row-${s.sale_no}`}>
                    <div className="min-w-0 flex-1">
                      <div className="flex items-center gap-2">
                        <p className="text-sm font-semibold">{s.table_number}</p>
                        <Badge variant="outline" className={`rounded-full text-[10px] ${statusTone[s.status] || ""}`}>
                          {s.status}
                        </Badge>
                      </div>
                      <p className="text-xs text-muted-foreground">
                        {s.created_at ? new Date(s.created_at).toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" }) : ""} · {payMethodLabel(s.payment_method)}{s.sold_by ? ` · ${s.sold_by}` : ""}
                      </p>
                    </div>
                    <p className="text-sm font-bold tabular-nums">{formatPrice(s.total)}</p>
                    <div className="flex items-center gap-1.5">
                      <Button variant="outline" size="sm" className="rounded-lg" onClick={() => printSale(saleFromRecent(s))} data-testid={`retail-reprint-button-${s.sale_no}`}>
                        <Printer className="h-3.5 w-3.5" />
                      </Button>
                      {!s.voided ? (
                        <Button
                          variant="outline"
                          size="sm"
                          className="rounded-lg border-destructive/40 text-destructive hover:bg-destructive/10 hover:text-destructive"
                          onClick={() => setVoidDialog({ open: true, sale: saleFromRecent(s), pin: "", busy: false })}
                          data-testid={`retail-void-button-${s.sale_no}`}
                        >
                          Void
                        </Button>
                      ) : null}
                    </div>
                  </div>
                ))}
              </div>
            )}
          </ScrollArea>
        </DialogContent>
      </Dialog>

      {/* ── Void PIN dialog ── */}
      <Dialog open={voidDialog.open} onOpenChange={(open) => !open && setVoidDialog({ open: false, sale: null, pin: "", busy: false })}>
        <DialogContent className="max-w-sm rounded-2xl">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <ShieldAlert className="h-5 w-5 text-destructive" />
              Void {voidDialog.sale?.table_number}
            </DialogTitle>
            <DialogDescription>
              This removes the sale everywhere (including kitchen/bar) and is recorded in the audit log. Enter the manager PIN to confirm.
            </DialogDescription>
          </DialogHeader>
          <Input
            type="password"
            inputMode="numeric"
            autoFocus
            placeholder="Manager PIN"
            value={voidDialog.pin}
            onChange={(e) => setVoidDialog((v) => ({ ...v, pin: e.target.value }))}
            onKeyDown={(e) => { if (e.key === "Enter") submitVoid(); }}
            className="h-11 rounded-xl"
            data-testid="retail-void-pin-input"
          />
          <DialogFooter>
            <Button variant="outline" className="rounded-xl" onClick={() => setVoidDialog({ open: false, sale: null, pin: "", busy: false })} data-testid="retail-void-cancel-button">
              Cancel
            </Button>
            <Button
              variant="destructive"
              className="rounded-xl"
              disabled={voidDialog.busy || !voidDialog.pin.trim()}
              onClick={submitVoid}
              data-testid="retail-void-confirm-button"
            >
              {voidDialog.busy ? <Loader2 className="h-4 w-4 animate-spin" /> : null}
              Void sale
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* ── Session 30.16 — "Are you sure?" before any payment is recorded ── */}
      <ConfirmPaymentDialog
        state={confirmPay}
        busy={busy || settle.busy}
        onCancel={() => setConfirmPay({ open: false })}
        onConfirm={() => { if (confirmPay.kind === "tab") settleTab(); else completeSale(); }}
      />

      {/* ── Printer setup (per-device slip printer) ── */}
      <PrinterSettingsDialog open={printerOpen} onOpenChange={setPrinterOpen} businessName={business?.name} formatPrice={formatPrice} />
    </div>
  );
}
